export const FX = (() => {
  'use strict';
  const TD = typeof TextDecoder !== 'undefined' ? new TextDecoder() : null;
  const dec = (u8) => TD.decode(u8);

  // ---------------------------------------------------------------- STL
  function parseSTL(buf) {
    const dv = new DataView(buf);
    const len = buf.byteLength;
    let binary = false;
    if (len >= 84) {
      const n = dv.getUint32(80, true);
      if (84 + n * 50 === len) binary = true;
      else {
        const head = dec(new Uint8Array(buf, 0, Math.min(1024, len)));
        binary = !/^\s*solid[\s\S]*?(facet|endsolid)/i.test(head);
      }
    }
    if (binary) {
      let n = dv.getUint32(80, true);
      n = Math.min(n, Math.floor((len - 84) / 50));
      const out = new Float32Array(n * 9);
      let o = 84;
      for (let i = 0; i < n; i++) {
        o += 12;
        for (let k = 0; k < 9; k++) { out[i * 9 + k] = dv.getFloat32(o, true); o += 4; }
        o += 2;
      }
      return out;
    }
    const text = dec(new Uint8Array(buf));
    const re = /vertex\s+([-+\d.eE]+)\s+([-+\d.eE]+)\s+([-+\d.eE]+)/g;
    const arr = []; let m;
    while ((m = re.exec(text))) arr.push(+m[1], +m[2], +m[3]);
    arr.length -= arr.length % 9;
    return new Float32Array(arr);
  }

  // ---------------------------------------------------------------- ZIP
  async function inflateRaw(u8) {
    const ds = new DecompressionStream('deflate-raw');
    const stream = new Blob([u8]).stream().pipeThrough(ds);
    return new Uint8Array(await new Response(stream).arrayBuffer());
  }
  function unzip(buf) {
    const u8 = new Uint8Array(buf), dv = new DataView(buf);
    let eocd = -1;
    for (let i = u8.length - 22; i >= Math.max(0, u8.length - 65557); i--) {
      if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
    }
    if (eocd < 0) throw new Error('This 3MF file is not a valid zip archive.');
    const count = dv.getUint16(eocd + 10, true);
    let p = dv.getUint32(eocd + 16, true);
    const files = {};
    for (let i = 0; i < count; i++) {
      if (dv.getUint32(p, true) !== 0x02014b50) break;
      const method = dv.getUint16(p + 10, true);
      const csize = dv.getUint32(p + 20, true);
      const nlen = dv.getUint16(p + 28, true), xlen = dv.getUint16(p + 30, true), clen = dv.getUint16(p + 32, true);
      const lho = dv.getUint32(p + 42, true);
      const name = dec(u8.subarray(p + 46, p + 46 + nlen));
      p += 46 + nlen + xlen + clen;
      files[name] = { method, csize, lho };
    }
    return {
      names: Object.keys(files),
      async read(name) {
        const f = files[name];
        const nl = dv.getUint16(f.lho + 26, true), xl = dv.getUint16(f.lho + 28, true);
        const start = f.lho + 30 + nl + xl;
        const data = u8.subarray(start, start + f.csize);
        if (f.method === 0) return data.slice();
        if (f.method === 8) return inflateRaw(data);
        throw new Error('Unsupported zip compression method ' + f.method);
      }
    };
  }
  const CRC_T = (() => { const t = new Uint32Array(256); for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; } return t; })();
  function crc32(u8) { let c = 0xffffffff; for (let i = 0; i < u8.length; i++) c = CRC_T[(c ^ u8[i]) & 255] ^ (c >>> 8); return (c ^ 0xffffffff) >>> 0; }
  // Store-only zip writer. entries: [{name, data:Uint8Array}]
  // Store-only zip writer. entries: [{name, data:Uint8Array}]
  function zip(entries) {
    const enc = new TextEncoder();
    const parts = []; const central = []; let offset = 0;
    for (const e of entries) {
      const name = enc.encode(e.name), data = e.data, crc = crc32(data);
      const h = new DataView(new ArrayBuffer(30));
      h.setUint32(0, 0x04034b50, true); h.setUint16(4, 20, true); h.setUint16(6, 0x0800, true); h.setUint16(8, 0, true);
      h.setUint16(10, 0, true); h.setUint16(12, 0x21, true); h.setUint32(14, crc, true);
      h.setUint32(18, data.length, true); h.setUint32(22, data.length, true); h.setUint16(26, name.length, true); h.setUint16(28, 0, true);
      parts.push(new Uint8Array(h.buffer), name, data);
      const c = new DataView(new ArrayBuffer(46));
      c.setUint32(0, 0x02014b50, true); c.setUint16(4, 20, true); c.setUint16(6, 20, true); c.setUint16(8, 0x0800, true);
      c.setUint16(10, 0, true); c.setUint16(12, 0, true); c.setUint16(14, 0x21, true); c.setUint32(16, crc, true);
      c.setUint32(20, data.length, true); c.setUint32(24, data.length, true); c.setUint16(28, name.length, true);
      c.setUint32(42, offset, true);
      central.push(new Uint8Array(c.buffer), name);
      offset += 30 + name.length + data.length;
    }
    const cdSize = central.reduce((s, a) => s + a.length, 0);
    const end = new DataView(new ArrayBuffer(22));
    end.setUint32(0, 0x06054b50, true); end.setUint16(8, entries.length, true); end.setUint16(10, entries.length, true);
    end.setUint32(12, cdSize, true); end.setUint32(16, offset, true);
    const all = [...parts, ...central, new Uint8Array(end.buffer)];
    const out = new Uint8Array(all.reduce((s, a) => s + a.length, 0));
    let o = 0; for (const a of all) { out.set(a, o); o += a.length; }
    return out;
  }

  // ---------------------------------------------------------------- 3MF
  const ID = [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0];
  function parseTransform(s) { if (!s) return ID.slice(); const a = s.trim().split(/\s+/).map(Number); return a.length === 12 && a.every(isFinite) ? a : ID.slice(); }
  // p' = p\xB7A\xB7B  (3MF row-vector convention)
  function mulT(A, B) {
    const r = new Array(12);
    for (let i = 0; i < 4; i++) for (let j = 0; j < 3; j++) {
      let s = 0; for (let k = 0; k < 3; k++) s += A[i * 3 + k] * B[k * 3 + j];
      if (i === 3) s += B[9 + j];
      r[i * 3 + j] = s;
    }
    return r;
  }
  const attr = (s, name) => { const m = s.match(new RegExp('(?:^|\\s)(?:[\\w]+:)?' + name + '\\s*=\\s*"([^"]*)"')); return m ? m[1] : null; };
  function parseModelXML(txt) {
    const unitM = txt.match(/<(?:\w+:)?model\b[^>]*\bunit\s*=\s*"(\w+)"/);
    const units = { micron: 0.001, millimeter: 1, centimeter: 10, inch: 25.4, foot: 304.8, meter: 1000 };
    const scale = unitM ? (units[unitM[1]] || 1) : 1;
    const objects = {};
    const objRe = /<(?:\w+:)?object\b([^>]*)>([\s\S]*?)<\/(?:\w+:)?object>/g;
    let m;
    while ((m = objRe.exec(txt))) {
      const id = attr(m[1], 'id'); const body = m[2];
      const obj = { mesh: null, components: [] };
      const vs = []; const ts = [];
      const vRe = /<(?:\w+:)?vertex\b([^>]*)\/?>/g; let v;
      while ((v = vRe.exec(body))) vs.push(+attr(v[1], 'x'), +attr(v[1], 'y'), +attr(v[1], 'z'));
      const tRe = /<(?:\w+:)?triangle\b([^>]*)\/?>/g; let t;
      while ((t = tRe.exec(body))) ts.push(+attr(t[1], 'v1'), +attr(t[1], 'v2'), +attr(t[1], 'v3'));
      if (vs.length && ts.length) obj.mesh = { v: vs, t: ts };
      const cRe = /<(?:\w+:)?component\b([^>]*)\/?>/g; let c;
      while ((c = cRe.exec(body))) obj.components.push({ objectid: attr(c[1], 'objectid'), transform: parseTransform(attr(c[1], 'transform')), path: attr(c[1], 'path') });
      objects[id] = obj;
    }
    const build = [];
    const iRe = /<(?:\w+:)?item\b([^>]*)\/?>/g;
    while ((m = iRe.exec(txt))) build.push({ objectid: attr(m[1], 'objectid'), transform: parseTransform(attr(m[1], 'transform')), path: attr(m[1], 'path') });
    return { objects, build, scale };
  }
  async function parse3MF(buf) {
    const z = unzip(buf);
    const find = (path) => { const p = path.replace(/^\//, '').toLowerCase(); return z.names.find((n) => n.toLowerCase() === p); };
    const cache = {};
    async function load(path) {
      if (cache[path]) return cache[path];
      const nm = find(path);
      if (!nm) throw new Error('3MF part missing: ' + path);
      return (cache[path] = parseModelXML(dec(await z.read(nm))));
    }
    let root = '/3D/3dmodel.model';
    const relsName = find('_rels/.rels');
    if (relsName) {
      const t = dec(await z.read(relsName));
      const rel = [...t.matchAll(/<Relationship\b([^>]*)\/?>/g)].map((r) => r[1]).find((r) => /3dmodel/i.test(attr(r, 'Type') || ''));
      if (rel && attr(rel, 'Target')) root = attr(rel, 'Target');
    }
    if (!find(root)) { const any = z.names.find((n) => /\.model$/i.test(n)); if (!any) throw new Error('No 3D model found inside this 3MF.'); root = any; }
    const doc = await load(root);
    const out = [];
    async function emit(d, id, M, depth) {
      const obj = d.objects[id];
      if (!obj || depth > 20) return;
      if (obj.mesh) {
        const { v, t } = obj.mesh; const s = d.scale;
        for (let i = 0; i < t.length; i++) {
          const k = t[i] * 3; const x = v[k] * s, y = v[k + 1] * s, zz = v[k + 2] * s;
          out.push(x * M[0] + y * M[3] + zz * M[6] + M[9] * s, x * M[1] + y * M[4] + zz * M[7] + M[10] * s, x * M[2] + y * M[5] + zz * M[8] + M[11] * s);
        }
      }
      for (const c of obj.components) {
        const cd = c.path ? await load(c.path) : d;
        await emit(cd, c.objectid, mulT(c.transform, M), depth + 1);
      }
    }
    if (doc.build.length) for (const it of doc.build) { const d = it.path ? await load(it.path) : doc; await emit(d, it.objectid, it.transform, 0); }
    else for (const id in doc.objects) if (doc.objects[id].mesh) await emit(doc, id, ID, 0);
    if (!out.length) throw new Error('This 3MF contains no triangles.');
    return new Float32Array(out);
  }

  // ---------------------------------------------------------------- export
  function toSTL(soups) {
    let n = 0; for (const s of soups) n += s.length / 9;
    const buf = new ArrayBuffer(84 + n * 50), dv = new DataView(buf);
    const hdr = 'JumpStart 3D FlexiGen print-in-place export';
    for (let i = 0; i < hdr.length; i++) dv.setUint8(i, hdr.charCodeAt(i));
    dv.setUint32(80, n, true);
    let o = 84;
    for (const s of soups) for (let i = 0; i < s.length; i += 9) {
      const ax = s[i + 3] - s[i], ay = s[i + 4] - s[i + 1], az = s[i + 5] - s[i + 2];
      const bx = s[i + 6] - s[i], by = s[i + 7] - s[i + 1], bz = s[i + 8] - s[i + 2];
      let nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx;
      const l = Math.hypot(nx, ny, nz) || 1;
      dv.setFloat32(o, nx / l, true); dv.setFloat32(o + 4, ny / l, true); dv.setFloat32(o + 8, nz / l, true); o += 12;
      for (let k = 0; k < 9; k++) { dv.setFloat32(o, s[i + k], true); o += 4; }
      o += 2;
    }
    return new Uint8Array(buf);
  }
  function to3MF(objects) { // objects: [{name, soup}]
    const enc = new TextEncoder();
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02">\n<metadata name="Application">Super Flexi Builder</metadata>\n<resources>\n';
    const chunks = [];
    objects.forEach((ob, idx) => {
      const w = weld(ob.soup, 1e-4);
      const vs = []; for (let i = 0; i < w.P.length; i += 3) vs.push('<vertex x="' + +w.P[i].toFixed(5) + '" y="' + +w.P[i + 1].toFixed(5) + '" z="' + +w.P[i + 2].toFixed(5) + '"/>');
      const ts = []; for (let i = 0; i < w.T.length; i += 3) ts.push('<triangle v1="' + w.T[i] + '" v2="' + w.T[i + 1] + '" v3="' + w.T[i + 2] + '"/>');
      chunks.push('<object id="' + (idx + 1) + '" name="' + ob.name.replace(/[<>&"]/g, '') + '" type="model"><mesh><vertices>\n' + vs.join('\n') + '\n</vertices><triangles>\n' + ts.join('\n') + '\n</triangles></mesh></object>\n');
    });
    xml += chunks.join('') + '</resources>\n<build>\n' + objects.map((o, i) => '<item objectid="' + (i + 1) + '"/>').join('\n') + '\n</build>\n</model>\n';
    const ct = '<?xml version="1.0" encoding="UTF-8"?>\n<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/></Types>';
    const rels = '<?xml version="1.0" encoding="UTF-8"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/></Relationships>';
    return zip([{ name: '[Content_Types].xml', data: enc.encode(ct) }, { name: '_rels/.rels', data: enc.encode(rels) }, { name: '3D/3dmodel.model', data: enc.encode(xml) }]);
  }

  // ---------------------------------------------------------------- mesh
  function weld(soup, q) {
    const inv = 1 / q; const map = new Map(); const P = []; const T = [];
    const idx = new Int32Array(3);
    for (let i = 0; i < soup.length; i += 9) {
      for (let k = 0; k < 3; k++) {
        const x = soup[i + k * 3], y = soup[i + k * 3 + 1], z = soup[i + k * 3 + 2];
        const key = Math.round(x * inv) + ',' + Math.round(y * inv) + ',' + Math.round(z * inv);
        let id = map.get(key);
        if (id === undefined) { id = P.length / 3; map.set(key, id); P.push(x, y, z); }
        idx[k] = id;
      }
      if (idx[0] !== idx[1] && idx[1] !== idx[2] && idx[0] !== idx[2]) T.push(idx[0], idx[1], idx[2]);
    }
    return { P: new Float64Array(P), T: new Uint32Array(T) };
  }

  // Stitch T-junctions left by cutting: split triangles whose open edges have vertices lying on them
  function fixTJunctions(soup, passes = 2) {
    for (let pass = 0; pass < passes; pass++) {
      const { P, T } = weld(soup, 1e-4);
      const nv = P.length / 3, nt = T.length / 3;
      const dir = new Set();
      for (let i = 0; i < nt; i++) for (let k = 0; k < 3; k++) dir.add(T[i * 3 + k] * nv + T[i * 3 + (k + 1) % 3]);
      const isOpen = (a, b) => !dir.has(b * nv + a);
      const bverts = new Set();
      for (let i = 0; i < nt; i++) for (let k = 0; k < 3; k++) { const a = T[i * 3 + k], b = T[i * 3 + (k + 1) % 3]; if (isOpen(a, b)) { bverts.add(a); bverts.add(b); } }
      if (!bverts.size) return soup;
      const cs = 0.5, hash = new Map(), hk = (x, y, z) => x + ',' + y + ',' + z;
      for (const v of bverts) { const k = hk(Math.floor(P[v * 3] / cs), Math.floor(P[v * 3 + 1] / cs), Math.floor(P[v * 3 + 2] / cs)); const l = hash.get(k); if (l) l.push(v); else hash.set(k, [v]); }
      const onEdge = (a, b) => {
        const ax = P[a * 3], ay = P[a * 3 + 1], az = P[a * 3 + 2], dx = P[b * 3] - ax, dy = P[b * 3 + 1] - ay, dz = P[b * 3 + 2] - az;
        const L2 = dx * dx + dy * dy + dz * dz; if (L2 < 1e-12) return [];
        const res = [];
        // walk along the edge, looking in the cells it passes through (and their neighbours)
        const len = Math.sqrt(L2), steps = Math.max(1, Math.ceil(len / (cs * 0.5))), seenC = new Set(), seenV = new Set();
        for (let st = 0; st <= steps; st++) {
          const f = st / steps, cx = Math.floor((ax + dx * f) / cs), cy = Math.floor((ay + dy * f) / cs), cz = Math.floor((az + dz * f) / cs);
          for (let x = cx - 1; x <= cx + 1; x++) for (let y = cy - 1; y <= cy + 1; y++) for (let z = cz - 1; z <= cz + 1; z++) {
          const kk = hk(x, y, z); if (seenC.has(kk)) continue; seenC.add(kk);
          const l = hash.get(kk); if (!l) continue;
          for (const v of l) {
            if (v === a || v === b || seenV.has(v)) continue; seenV.add(v);
            const px = P[v * 3] - ax, py = P[v * 3 + 1] - ay, pz = P[v * 3 + 2] - az;
            const t = (px * dx + py * dy + pz * dz) / L2;
            if (t <= 1e-6 || t >= 1 - 1e-6) continue;
            const ex = px - t * dx, ey = py - t * dy, ez = pz - t * dz;
            if (ex * ex + ey * ey + ez * ez < 4e-8) res.push([t, v]);
          }
          }
        }
        return res.sort((p, q) => p[0] - q[0]).map((p) => p[1]);
      };
      const out = []; let changed = false;
      const pt = (v) => [P[v * 3], P[v * 3 + 1], P[v * 3 + 2]];
      for (let i = 0; i < nt; i++) {
        const tv = [T[i * 3], T[i * 3 + 1], T[i * 3 + 2]];
        const poly = []; let ins = 0;
        for (let k = 0; k < 3; k++) {
          const a = tv[k], b = tv[(k + 1) % 3];
          poly.push(a);
          if (isOpen(a, b)) { const e = onEdge(a, b); ins += e.length; poly.push(...e); }
        }
        if (!ins) { for (const v of tv) out.push(...pt(v)); continue; }
        changed = true;
        const c = [0, 0, 0]; for (const v of tv) { c[0] += P[v * 3] / 3; c[1] += P[v * 3 + 1] / 3; c[2] += P[v * 3 + 2] / 3; }
        for (let k = 0; k < poly.length; k++) out.push(...c, ...pt(poly[k]), ...pt(poly[(k + 1) % poly.length]));
      }
      soup = new Float32Array(out);
      if (!changed) return soup;
    }
    return soup;
  }

  // merge vertices closer than 0.1 \xB5m and drop the zero-area slivers that leaves
  function cleanSoup(soup) {
    const { P, T } = weld(soup, 1e-4), o = new Float32Array(T.length * 3);
    for (let i = 0; i < T.length; i++) { o[i * 3] = P[T[i] * 3]; o[i * 3 + 1] = P[T[i] * 3 + 1]; o[i * 3 + 2] = P[T[i] * 3 + 2]; }
    return o;
  }
  function makeMesh(soup) {
    const { P, T } = weld(soup, 1e-5);
    const nt = T.length / 3;
    // orientation: flip if signed volume negative
    let vol = 0;
    for (let i = 0; i < nt; i++) {
      const a = T[i * 3] * 3, b = T[i * 3 + 1] * 3, c = T[i * 3 + 2] * 3;
      vol += P[a] * (P[b + 1] * P[c + 2] - P[b + 2] * P[c + 1]) - P[a + 1] * (P[b] * P[c + 2] - P[b + 2] * P[c]) + P[a + 2] * (P[b] * P[c + 1] - P[b + 1] * P[c]);
    }
    if (vol < 0) for (let i = 0; i < nt; i++) { const t = T[i * 3 + 1]; T[i * 3 + 1] = T[i * 3 + 2]; T[i * 3 + 2] = t; }
    const mesh = { P, T, nt, nv: P.length / 3, volume: Math.abs(vol) / 6 };
    finishMesh(mesh);
    return mesh;
  }
  function finishMesh(mesh) {
    const { P, T, nt } = mesh;
    const N = new Float64Array(nt * 3), B = new Float64Array(nt * 6);
    let mnx = Infinity, mny = Infinity, mnz = Infinity, mxx = -Infinity, mxy = -Infinity, mxz = -Infinity;
    for (let i = 0; i < nt; i++) {
      const a = T[i * 3] * 3, b = T[i * 3 + 1] * 3, c = T[i * 3 + 2] * 3;
      const ux = P[b] - P[a], uy = P[b + 1] - P[a + 1], uz = P[b + 2] - P[a + 2];
      const vx = P[c] - P[a], vy = P[c + 1] - P[a + 1], vz = P[c + 2] - P[a + 2];
      let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      const l = Math.hypot(nx, ny, nz) || 1; N[i * 3] = nx / l; N[i * 3 + 1] = ny / l; N[i * 3 + 2] = nz / l;
      for (let d = 0; d < 3; d++) {
        const lo = Math.min(P[a + d], P[b + d], P[c + d]), hi = Math.max(P[a + d], P[b + d], P[c + d]);
        B[i * 6 + d] = lo; B[i * 6 + 3 + d] = hi;
      }
      mnx = Math.min(mnx, B[i * 6]); mny = Math.min(mny, B[i * 6 + 1]); mnz = Math.min(mnz, B[i * 6 + 2]);
      mxx = Math.max(mxx, B[i * 6 + 3]); mxy = Math.max(mxy, B[i * 6 + 4]); mxz = Math.max(mxz, B[i * 6 + 5]);
    }
    mesh.N = N; mesh.B = B;
    mesh.bbox = { min: [mnx, mny, mnz], max: [mxx, mxy, mxz] };
    buildGrid(mesh);
  }
  // Boundary / non-manifold edge count (quality report)
  function openEdges(mesh) {
    const { T, nt, nv } = mesh; const m = new Map();
    for (let i = 0; i < nt; i++) for (let k = 0; k < 3; k++) {
      const a = T[i * 3 + k], b = T[i * 3 + (k + 1) % 3];
      const key = a < b ? a * nv + b : b * nv + a;
      m.set(key, (m.get(key) || 0) + (a < b ? 1 : -1));
    }
    let bad = 0; for (const v of m.values()) if (v !== 0) bad++;
    return bad;
  }

  function buildGrid(mesh) {
    const { bbox, B, nt } = mesh;
    const dx = bbox.max[0] - bbox.min[0], dy = bbox.max[1] - bbox.min[1];
    const res = Math.max(8, Math.min(160, Math.round(Math.sqrt(nt) / 2)));
    const cs = Math.max(dx, dy, 1e-6) / res * 1.0001;
    const gx = Math.max(1, Math.ceil(dx / cs) || 1), gy = Math.max(1, Math.ceil(dy / cs) || 1);
    const counts = new Int32Array(gx * gy + 1);
    const x0 = bbox.min[0], y0 = bbox.min[1];
    const cx = (x) => Math.min(gx - 1, Math.max(0, Math.floor((x - x0) / cs)));
    const cy = (y) => Math.min(gy - 1, Math.max(0, Math.floor((y - y0) / cs)));
    for (let i = 0; i < nt; i++) {
      const a = cx(B[i * 6]), b = cx(B[i * 6 + 3]), c = cy(B[i * 6 + 1]), d = cy(B[i * 6 + 4]);
      for (let y = c; y <= d; y++) for (let x = a; x <= b; x++) counts[y * gx + x + 1]++;
    }
    for (let i = 1; i < counts.length; i++) counts[i] += counts[i - 1];
    const items = new Int32Array(counts[counts.length - 1]); const fill = counts.slice(0, gx * gy);
    for (let i = 0; i < nt; i++) {
      const a = cx(B[i * 6]), b = cx(B[i * 6 + 3]), c = cy(B[i * 6 + 1]), d = cy(B[i * 6 + 4]);
      for (let y = c; y <= d; y++) for (let x = a; x <= b; x++) items[fill[y * gx + x]++] = i;
    }
    mesh.grid = { x0, y0, cs, gx, gy, start: counts, items, cx, cy, stamp: new Uint32Array(nt), gen: 0 };
  }
  // visit each triangle whose bbox overlaps the xy rect once
  function queryRect(mesh, x0, y0, x1, y1, cb) {
    const g = mesh.grid; g.gen++; if (g.gen > 4e9) { g.stamp.fill(0); g.gen = 1; }
    const a = g.cx(x0), b = g.cx(x1), c = g.cy(y0), d = g.cy(y1);
    const B = mesh.B;
    for (let y = c; y <= d; y++) for (let x = a; x <= b; x++) {
      const ci = y * g.gx + x;
      for (let k = g.start[ci]; k < g.start[ci + 1]; k++) {
        const t = g.items[k];
        if (g.stamp[t] === g.gen) continue; g.stamp[t] = g.gen;
        if (B[t * 6] > x1 || B[t * 6 + 3] < x0 || B[t * 6 + 1] > y1 || B[t * 6 + 4] < y0) continue;
        cb(t);
      }
    }
  }
  // winding number along +z ray; > 0 means inside
  function inside(mesh, x, y, z) {
    x += 1.3719e-7; y += 2.7183e-7; z += 3.1416e-8;
    const g = mesh.grid;
    if (x < g.x0 || y < g.y0 || x > g.x0 + g.cs * g.gx || y > g.y0 + g.cs * g.gy) return false;
    const ci = g.cy(y) * g.gx + g.cx(x);
    const { P, T, N } = mesh; let w = 0;
    for (let k = g.start[ci]; k < g.start[ci + 1]; k++) {
      const t = g.items[k]; const nz = N[t * 3 + 2];
      if (nz === 0) continue;
      const a = T[t * 3] * 3, b = T[t * 3 + 1] * 3, c = T[t * 3 + 2] * 3;
      const ax = P[a] - x, ay = P[a + 1] - y, bx = P[b] - x, by = P[b + 1] - y, cx = P[c] - x, cy = P[c + 1] - y;
      const w0 = bx * cy - by * cx, w1 = cx * ay - cy * ax, w2 = ax * by - ay * bx;
      if (!((w0 >= 0 && w1 >= 0 && w2 >= 0) || (w0 <= 0 && w1 <= 0 && w2 <= 0))) continue;
      const s = w0 + w1 + w2; if (s === 0) continue;
      const zz = (w0 * P[a + 2] + w1 * P[b + 2] + w2 * P[c + 2]) / s;
      if (zz > z) w += nz > 0 ? 1 : -1;
    }
    return w > 0;
  }

  // ---------------------------------------------------------------- earcut (port of mapbox/earcut core)
  function earcut(data, holeIndices) {
    const hasHoles = holeIndices && holeIndices.length;
    const outerLen = hasHoles ? holeIndices[0] * 2 : data.length;
    let outer = linkedList(data, 0, outerLen, true);
    const tris = [];
    if (!outer || outer.next === outer.prev) return tris;
    if (hasHoles) outer = eliminateHoles(data, holeIndices, outer);
    earcutLinked(outer, tris, 0);
    return tris;
  }
  function Node(i, x, y) { this.i = i; this.x = x; this.y = y; this.prev = null; this.next = null; this.steiner = false; }
  function insertNode(i, x, y, last) { const p = new Node(i, x, y); if (!last) { p.prev = p; p.next = p; } else { p.next = last.next; p.prev = last; last.next.prev = p; last.next = p; } return p; }
  function removeNode(p) { p.next.prev = p.prev; p.prev.next = p.next; }
  function signedArea(data, start, end) { let s = 0; for (let i = start, j = end - 2; i < end; i += 2) { s += (data[j] - data[i]) * (data[i + 1] + data[j + 1]); j = i; } return s; }
  function linkedList(data, start, end, clockwise) {
    let last;
    if (clockwise === (signedArea(data, start, end) > 0)) for (let i = start; i < end; i += 2) last = insertNode(i / 2, data[i], data[i + 1], last);
    else for (let i = end - 2; i >= start; i -= 2) last = insertNode(i / 2, data[i], data[i + 1], last);
    if (last && equals(last, last.next)) { removeNode(last); last = last.next; }
    return last;
  }
  const equals = (a, b) => a.x === b.x && a.y === b.y;
  const area = (p, q, r) => (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
  const pit = (ax, ay, bx, by, cx, cy, px, py) => (cx - px) * (ay - py) >= (ax - px) * (cy - py) && (ax - px) * (by - py) >= (bx - px) * (ay - py) && (bx - px) * (cy - py) >= (cx - px) * (by - py);
  function filterPoints(start, end) {
    if (!start) return start; if (!end) end = start;
    let p = start, again;
    do {
      again = false;
      if (!p.steiner && (equals(p, p.next) || area(p.prev, p, p.next) === 0)) { removeNode(p); p = end = p.prev; if (p === p.next) break; again = true; }
      else p = p.next;
    } while (again || p !== end);
    return end;
  }
  function isEar(ear) {
    const a = ear.prev, b = ear, c = ear.next;
    if (area(a, b, c) >= 0) return false;
    const x0 = Math.min(a.x, b.x, c.x), y0 = Math.min(a.y, b.y, c.y), x1 = Math.max(a.x, b.x, c.x), y1 = Math.max(a.y, b.y, c.y);
    let p = c.next;
    while (p !== a) {
      if (p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1 && pit(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) && area(p.prev, p, p.next) >= 0) return false;
      p = p.next;
    }
    return true;
  }
  function earcutLinked(ear, tris, pass) {
    if (!ear) return;
    let stop = ear, guard = 0;
    while (ear.prev !== ear.next) {
      if (++guard > 5e7) return;
      const prev = ear.prev, next = ear.next;
      if (isEar(ear)) { tris.push(prev.i, ear.i, next.i); removeNode(ear); ear = next.next; stop = next.next; continue; }
      ear = next;
      if (ear === stop) {
        if (!pass) earcutLinked(filterPoints(ear), tris, 1);
        else if (pass === 1) { ear = cureLocalIntersections(filterPoints(ear), tris); earcutLinked(ear, tris, 2); }
        else if (pass === 2) splitEarcut(ear, tris);
        break;
      }
    }
  }
  function cureLocalIntersections(start, tris) {
    let p = start;
    do {
      const a = p.prev, b = p.next.next;
      if (!equals(a, b) && intersects(a, p, p.next, b) && locallyInside(a, b) && locallyInside(b, a)) {
        tris.push(a.i, p.i, b.i); removeNode(p); removeNode(p.next); p = start = b;
      }
      p = p.next;
    } while (p !== start);
    return filterPoints(p);
  }
  function splitEarcut(start, tris) {
    let a = start;
    do {
      let b = a.next.next;
      while (b !== a.prev) {
        if (a.i !== b.i && isValidDiagonal(a, b)) {
          let c = splitPolygon(a, b);
          a = filterPoints(a, a.next); c = filterPoints(c, c.next);
          earcutLinked(a, tris, 0); earcutLinked(c, tris, 0);
          return;
        }
        b = b.next;
      }
      a = a.next;
    } while (a !== start);
  }
  function eliminateHoles(data, holeIndices, outer) {
    const queue = [];
    for (let i = 0; i < holeIndices.length; i++) {
      const start = holeIndices[i] * 2, end = i < holeIndices.length - 1 ? holeIndices[i + 1] * 2 : data.length;
      const list = linkedList(data, start, end, false);
      if (!list) continue;
      if (list === list.next) list.steiner = true;
      queue.push(getLeftmost(list));
    }
    queue.sort((a, b) => a.x - b.x);
    for (const h of queue) outer = eliminateHole(h, outer);
    return outer;
  }
  function eliminateHole(hole, outer) {
    const bridge = findHoleBridge(hole, outer);
    if (!bridge) return outer;
    const br = splitPolygon(bridge, hole);
    filterPoints(br, br.next);
    return filterPoints(bridge, bridge.next);
  }
  function findHoleBridge(hole, outer) {
    let p = outer; const hx = hole.x, hy = hole.y; let qx = -Infinity, m;
    do {
      if (hy <= p.y && hy >= p.next.y && p.next.y !== p.y) {
        const x = p.x + (hy - p.y) * (p.next.x - p.x) / (p.next.y - p.y);
        if (x <= hx && x > qx) { qx = x; m = p.x < p.next.x ? p : p.next; if (x === hx) return m; }
      }
      p = p.next;
    } while (p !== outer);
    if (!m) return null;
    const stop = m, mx = m.x, my = m.y; let tanMin = Infinity;
    p = m;
    do {
      if (hx >= p.x && p.x >= mx && hx !== p.x && pit(hy < my ? hx : qx, hy, mx, my, hy < my ? qx : hx, hy, p.x, p.y)) {
        const tan = Math.abs(hy - p.y) / (hx - p.x);
        if (locallyInside(p, hole) && (tan < tanMin || (tan === tanMin && (p.x > m.x || (p.x === m.x && sectorContainsSector(m, p)))))) { m = p; tanMin = tan; }
      }
      p = p.next;
    } while (p !== stop);
    return m;
  }
  const sectorContainsSector = (m, p) => area(m.prev, m, p.prev) < 0 && area(p.next, m, m.next) < 0;
  function getLeftmost(start) { let p = start, l = start; do { if (p.x < l.x || (p.x === l.x && p.y < l.y)) l = p; p = p.next; } while (p !== start); return l; }
  function isValidDiagonal(a, b) {
    return a.next.i !== b.i && a.prev.i !== b.i && !intersectsPolygon(a, b) &&
      ((locallyInside(a, b) && locallyInside(b, a) && middleInside(a, b) && (area(a.prev, a, b.prev) || area(a, b.prev, b))) ||
        (equals(a, b) && area(a.prev, a, a.next) > 0 && area(b.prev, b, b.next) > 0));
  }
  const sgn = (v) => (v > 0 ? 1 : v < 0 ? -1 : 0);
  const onSeg = (p, q, r) => q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x) && q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y);
  function intersects(p1, q1, p2, q2) {
    const o1 = sgn(area(p1, q1, p2)), o2 = sgn(area(p1, q1, q2)), o3 = sgn(area(p2, q2, p1)), o4 = sgn(area(p2, q2, q1));
    if (o1 !== o2 && o3 !== o4) return true;
    if (o1 === 0 && onSeg(p1, p2, q1)) return true;
    if (o2 === 0 && onSeg(p1, q2, q1)) return true;
    if (o3 === 0 && onSeg(p2, p1, q2)) return true;
    if (o4 === 0 && onSeg(p2, q1, q2)) return true;
    return false;
  }
  function intersectsPolygon(a, b) {
    let p = a;
    do { if (p.i !== a.i && p.next.i !== a.i && p.i !== b.i && p.next.i !== b.i && intersects(p, p.next, a, b)) return true; p = p.next; } while (p !== a);
    return false;
  }
  const locallyInside = (a, b) => area(a.prev, a, a.next) < 0 ? area(a, b, a.next) >= 0 && area(a, a.prev, b) >= 0 : area(a, b, a.prev) < 0 || area(a, a.next, b) < 0;
  function middleInside(a, b) {
    let p = a, inside = false; const px = (a.x + b.x) / 2, py = (a.y + b.y) / 2;
    do { if (((p.y > py) !== (p.next.y > py)) && p.next.y !== p.y && (px < (p.next.x - p.x) * (py - p.y) / (p.next.y - p.y) + p.x)) inside = !inside; p = p.next; } while (p !== a);
    return inside;
  }
  function splitPolygon(a, b) {
    const a2 = new Node(a.i, a.x, a.y), b2 = new Node(b.i, b.x, b.y), an = a.next, bp = b.prev;
    a.next = b; b.prev = a; a2.next = an; an.prev = a2; b2.next = a2; a2.prev = b2; bp.next = b2; b2.prev = bp;
    return b2;
  }

  // ---------------------------------------------------------------- 2D helpers
  function polyArea2(p) { let s = 0; for (let i = 0, n = p.length / 2, j = n - 1; i < n; j = i++) s += p[j * 2] * p[i * 2 + 1] - p[i * 2] * p[j * 2 + 1]; return s / 2; }
  function pointInPoly(p, x, y) {
    let c = false;
    for (let i = 0, n = p.length / 2, j = n - 1; i < n; j = i++) {
      const xi = p[i * 2], yi = p[i * 2 + 1], xj = p[j * 2], yj = p[j * 2 + 1];
      if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) c = !c;
    }
    return c;
  }
  // clip convex/any polygon (array of [x,y]) by half-plane a*x+b*y <= c
  function clipHalf(poly, a, b, c) {
    const out = []; const n = poly.length;
    for (let i = 0; i < n; i++) {
      const P = poly[i], Q = poly[(i + 1) % n];
      const dp = a * P[0] + b * P[1] - c, dq = a * Q[0] + b * Q[1] - c;
      if (dp <= 0) out.push(P);
      if ((dp < 0 && dq > 0) || (dp > 0 && dq < 0)) { const t = dp / (dp - dq); out.push([P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t]); }
    }
    return out;
  }
  // clip polygon by convex CCW polygon
  function clipConvex(poly, conv) {
    let out = poly;
    for (let i = 0; i < conv.length && out.length; i++) {
      const A = conv[i], B = conv[(i + 1) % conv.length];
      // inside = left of A->B : (B-A) x (P-A) >= 0  -> -(dy)*x + dx*y ... write as a*x+b*y<=c
      const dx = B[0] - A[0], dy = B[1] - A[1];
      out = clipHalf(out, dy, -dx, dy * A[0] - dx * A[1]);
    }
    return out;
  }
  // parameter interval [t0,t1] of segment A->B inside convex CCW polygon
  function segInConvex(A, B, conv) {
    let t0 = 0, t1 = 1;
    for (let i = 0; i < conv.length; i++) {
      const P = conv[i], Q = conv[(i + 1) % conv.length];
      const ex = Q[0] - P[0], ey = Q[1] - P[1];
      // f(t) = ex*(y - Py) - ey*(x - Px) >= 0 inside
      const fa = ex * (A[1] - P[1]) - ey * (A[0] - P[0]);
      const fb = ex * (B[1] - P[1]) - ey * (B[0] - P[0]);
      if (fa < 0 && fb < 0) return null;
      if (fa < 0) t0 = Math.max(t0, fa / (fa - fb));
      else if (fb < 0) t1 = Math.min(t1, fa / (fa - fb));
      if (t0 >= t1) return null;
    }
    return [t0, t1];
  }

  // ---------------------------------------------------------------- plane sections
  function planeFrame(N, d) {
    let u, v;
    if (Math.abs(N[2]) > 0.999999) { u = [1, 0, 0]; v = N[2] > 0 ? [0, 1, 0] : [0, -1, 0]; }
    else if (Math.abs(N[2]) < 1e-12) { u = [-N[1], N[0], 0]; v = [0, 0, 1]; }
    else { const l = Math.hypot(N[0], N[1]); u = [-N[1] / l, N[0] / l, 0]; v = [N[1] * u[2] - N[2] * u[1], N[2] * u[0] - N[0] * u[2], N[0] * u[1] - N[1] * u[0]]; }
    return { N, d, O: [N[0] * d, N[1] * d, N[2] * d], u, v };
  }
  function to2(F, x, y, z) { const dx = x - F.O[0], dy = y - F.O[1], dz = z - F.O[2]; return [dx * F.u[0] + dy * F.u[1] + dz * F.u[2], dx * F.v[0] + dy * F.v[1] + dz * F.v[2]]; }
  function to3(F, a, b) { return [F.O[0] + a * F.u[0] + b * F.v[0], F.O[1] + a * F.u[1] + b * F.v[1], F.O[2] + a * F.u[2] + b * F.v[2]]; }

  // segments of mesh \u2229 plane, oriented with solid on the left (in frame u,v)
  function crossSegment(mesh, F, t, out) {
    const { P, T, N: TN, nv } = mesh; const N = F.N, d = F.d;
    const vi = [T[t * 3], T[t * 3 + 1], T[t * 3 + 2]];
    const s = vi.map((v) => P[v * 3] * N[0] + P[v * 3 + 1] * N[1] + P[v * 3 + 2] * N[2] - d);
    const pos = s.map((x) => x >= 0);
    if (pos[0] === pos[1] && pos[1] === pos[2]) return false;
    const pts = [], keys = [];
    for (let k = 0; k < 3; k++) {
      const k2 = (k + 1) % 3;
      if (pos[k] === pos[k2]) continue;
      let a = vi[k], b = vi[k2], sa = s[k], sb = s[k2];
      if (a > b) { [a, b] = [b, a]; [sa, sb] = [sb, sa]; }
      const f = sa / (sa - sb);
      const x = P[a * 3] + (P[b * 3] - P[a * 3]) * f, y = P[a * 3 + 1] + (P[b * 3 + 1] - P[a * 3 + 1]) * f, z = P[a * 3 + 2] + (P[b * 3 + 2] - P[a * 3 + 2]) * f;
      pts.push(to2(F, x, y, z)); keys.push(a * nv + b);
    }
    // direction = N x m, m = tri normal projected
    const n0 = TN[t * 3], n1 = TN[t * 3 + 1], n2 = TN[t * 3 + 2];
    const dn = n0 * N[0] + n1 * N[1] + n2 * N[2];
    const m = [n0 - dn * N[0], n1 - dn * N[1], n2 - dn * N[2]];
    const dir = [N[1] * m[2] - N[2] * m[1], N[2] * m[0] - N[0] * m[2], N[0] * m[1] - N[1] * m[0]];
    const du = dir[0] * F.u[0] + dir[1] * F.u[1] + dir[2] * F.u[2], dv = dir[0] * F.v[0] + dir[1] * F.v[1] + dir[2] * F.v[2];
    const ex = pts[1][0] - pts[0][0], ey = pts[1][1] - pts[0][1];
    if (ex * du + ey * dv < 0) { pts.reverse(); keys.reverse(); }
    out.push({ a: pts[0], b: pts[1], ka: keys[0], kb: keys[1] });
    return true;
  }
  function planeTriangles(mesh, F, cb) {
    const N = F.N, d = F.d, B = mesh.B;
    const g = mesh.grid;
    if (Math.abs(N[2]) < 1e-12) {
      // vertical plane: cells straddling the line
      const cand = new Set();
      for (let y = 0; y < g.gy; y++) for (let x = 0; x < g.gx; x++) {
        const X0 = g.x0 + x * g.cs, Y0 = g.y0 + y * g.cs, X1 = X0 + g.cs, Y1 = Y0 + g.cs;
        const f = (X, Y) => X * N[0] + Y * N[1] - d;
        const a = f(X0, Y0), b = f(X1, Y0), c = f(X0, Y1), e = f(X1, Y1);
        if (Math.min(a, b, c, e) > 0 || Math.max(a, b, c, e) < 0) continue;
        const ci = y * g.gx + x;
        for (let k = g.start[ci]; k < g.start[ci + 1]; k++) cand.add(g.items[k]);
      }
      for (const t of cand) cb(t);
    } else {
      for (let t = 0; t < mesh.nt; t++) {
        if (Math.abs(N[2]) > 0.999999) { const z = d * N[2]; if (B[t * 6 + 2] > z || B[t * 6 + 5] < z) continue; }
        cb(t);
      }
    }
  }
  // full section: triangulated solid region in plane coords (CCW triangles, flat [x0,y0,x1,y1,x2,y2,...])
  function sectionLoops(mesh, F) {
    const segs0 = [];
    planeTriangles(mesh, F, (t) => crossSegment(mesh, F, t, segs0));
    // zero-length segments (vertices on the plane) join their two edge keys instead
    const parent = new Map();
    const find = (k) => { let r = k; while (parent.has(r)) r = parent.get(r); while (parent.has(k)) { const n = parent.get(k); parent.set(k, r); k = n; } return r; };
    const segs = [];
    for (const s of segs0) {
      if (Math.abs(s.a[0] - s.b[0]) < 1e-12 && Math.abs(s.a[1] - s.b[1]) < 1e-12) { const x = find(s.ka), y = find(s.kb); if (x !== y) parent.set(x, y); }
      else segs.push(s);
    }
    for (const s of segs) { s.ka = find(s.ka); s.kb = find(s.kb); }
    const byStart = new Map();
    segs.forEach((s, i) => { const l = byStart.get(s.ka); if (l) l.push(i); else byStart.set(s.ka, [i]); });
    const used = new Uint8Array(segs.length); const loops = []; let open = 0;
    for (let i = 0; i < segs.length; i++) {
      if (used[i]) continue;
      const loop = []; let cur = i; let closed = false;
      const startKey = segs[i].ka;
      for (let guard = 0; guard <= segs.length; guard++) {
        used[cur] = 1; const s = segs[cur];
        loop.push(s.a[0], s.a[1]);
        if (s.kb === startKey) { closed = true; break; }
        const nx = byStart.get(s.kb); let found = -1;
        if (nx) for (const j of nx) if (!used[j]) { found = j; break; }
        if (found < 0) break;
        cur = found;
      }
      if (closed && loop.length >= 6) loops.push(loop); else open++;
    }
    return { loops, open };
  }
  function triangulateLoops(loops) {
    const clean = [];
    for (const L of loops) {
      const o = [];
      for (let i = 0; i < L.length; i += 2) {
        const n = o.length;
        if (n && Math.abs(o[n - 2] - L[i]) < 1e-9 && Math.abs(o[n - 1] - L[i + 1]) < 1e-9) continue;
        o.push(L[i], L[i + 1]);
      }
      if (o.length >= 6 && Math.abs(o[0] - o[o.length - 2]) < 1e-9 && Math.abs(o[1] - o[o.length - 1]) < 1e-9) o.length -= 2;
      if (o.length >= 6) { const a = polyArea2(o); if (Math.abs(a) > 1e-10) clean.push({ p: o, a }); }
    }
    const outers = clean.filter((l) => l.a > 0), holes = clean.filter((l) => l.a < 0);
    outers.forEach((o) => (o.holes = []));
    for (const h of holes) {
      let best = null;
      for (const o of outers) if ((!best || o.a < best.a) && pointInPoly(o.p, h.p[0], h.p[1])) best = o;
      if (best) best.holes.push(h);
    }
    const tris = [];
    for (const o of outers) {
      const data = o.p.slice(); const hi = [];
      for (const h of o.holes) { hi.push(data.length / 2); for (const v of h.p) data.push(v); }
      const idx = earcut(data, hi);
      for (let i = 0; i < idx.length; i += 3) {
        const a = idx[i], b = idx[i + 1], c = idx[i + 2];
        let t = [data[a * 2], data[a * 2 + 1], data[b * 2], data[b * 2 + 1], data[c * 2], data[c * 2 + 1]];
        const ar = (t[2] - t[0]) * (t[5] - t[1]) - (t[4] - t[0]) * (t[3] - t[1]);
        if (ar === 0) continue;
        if (ar < 0) t = [t[0], t[1], t[4], t[5], t[2], t[3]];
        tris.push(t);
      }
    }
    return tris;
  }

  // ---------------------------------------------------------------- segment building
  const dot3 = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

  // split convex polygon (array of 3D points) by plane N\xB7p = d
  function splitPoly(poly, N, d) {
    const f = [], b = []; const n = poly.length; const e = 1e-10;
    const s = poly.map((p) => dot3(N, p) - d);
    for (let i = 0; i < n; i++) {
      const P = poly[i], Q = poly[(i + 1) % n], sp = s[i], sq = s[(i + 1) % n];
      if (sp >= -e) f.push(P); if (sp <= e) b.push(P);
      if ((sp > e && sq < -e) || (sp < -e && sq > e)) {
        const t = sp / (sp - sq); const X = [P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t, P[2] + (Q[2] - P[2]) * t];
        f.push(X); b.push(X);
      }
    }
    return [f, b];
  }

  // ================================================================ joint frames & zones
  // Every joint has its own frame: W is the hinge axis, (U,V) the plane it turns in.
  // Its zone is a stack of regular 48-gons in (U,V) (radii are apothems) cut into
  // bands by levels along W. Who owns each piece is decided by jointOwn().
  const SEG = 48, COSH = Math.cos(Math.PI / SEG), TAU = Math.PI * 2;
  function frameV(px, py) { return { O: [px, py, 0], U: [1, 0, 0], V: [0, 1, 0], W: [0, 0, 1] }; }
  function toL(F, x, y, z) {
    const dx = x - F.O[0], dy = y - F.O[1], dz = z - F.O[2];
    return [dx * F.U[0] + dy * F.U[1] + dz * F.U[2], dx * F.V[0] + dy * F.V[1] + dz * F.V[2], dx * F.W[0] + dy * F.W[1] + dz * F.W[2]];
  }
  function toG(F, u, v, w) {
    return [F.O[0] + u * F.U[0] + v * F.V[0] + w * F.W[0], F.O[1] + u * F.U[1] + v * F.V[1] + w * F.W[1], F.O[2] + u * F.U[2] + v * F.V[2] + w * F.W[2]];
  }
  function pvL(a, i) { const ang = (TAU * i) / SEG, R = a / COSH; return [R * Math.cos(ang), R * Math.sin(ang)]; }
  function inPolyL(a, u, v) {
    const d2 = u * u + v * v;
    if (d2 <= a * a) return true;
    const Rc = a / COSH; if (d2 >= Rc * Rc) return false;
    let ang = Math.atan2(v, u); if (ang < 0) ang += TAU;
    const i = Math.floor(ang / (TAU / SEG)) % SEG, mid = ((i + 0.5) * TAU) / SEG;
    return u * Math.cos(mid) + v * Math.sin(mid) < a;
  }
  function uniqSorted(a) { a = a.filter((v) => isFinite(v)).sort((x, y) => x - y); const o = []; for (const v of a) if (!o.length || v - o[o.length - 1] > 1e-9) o.push(v); return o; }
  function bandOwner(J, w) { for (const b of J.bands) if (w > b[0] && w < b[1]) return b[2]; return null; }
  function bbOf(pts) {
    const b = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];
    for (const p of pts) for (let k = 0; k < 3; k++) { if (p[k] < b[k]) b[k] = p[k]; if (p[k] > b[k + 3]) b[k + 3] = p[k]; }
    return b;
  }
  const bbHit = (a, b, e) => !(a[3] < b[0] - e || a[0] > b[3] + e || a[4] < b[1] - e || a[1] > b[4] + e || a[5] < b[2] - e || a[2] > b[5] + e);

  // Ownership near joint J for a segment playing \`role\` ('A' = the -n side, 'B' = the +n side).
  // inSeg: whether the point is in this segment by the cut map. undefined = the joint has no say.
  function jointOwn(J, role, inSeg, x, y, z) {
    const L = toL(J.F, x, y, z), u = L[0], v = L[1], w = L[2];
    if (w <= J.wLo || w >= J.wHi) return undefined;
    if (u * u + v * v >= J.rmax * J.rmax) return undefined;
    switch (J.style) {
      case 'lap': case 'tab': {
        if (!inPolyL(J.ro, u, v)) return undefined;
        if (inPolyL(J.ri, u, v)) {
          if (role === 'A') return w < J.zAt;
          if (w <= J.zBb) return false;
          if (w < J.zf && inPolyL(J.rStemCav, u, v)) return false;
          if (w >= J.zf && w < J.zCavTop && inPolyL(J.rHeadCav, u, v)) return false;
          return true;
        }
        return role === 'A' ? inSeg && w < J.zAt : inSeg && w > J.zBb;
      }
      case 'ball': {
        if (!inPolyL(J.ro, u, v)) return undefined;
        if (role === 'A') return false;
        if (!inPolyL(J.ri, u, v)) return inSeg;
        if (w > J.cav[0] && w < J.cav[1] && inPolyL(J.rCav, u, v)) return false;
        if (w > J.slot[0] && w < J.slot[1] && J.m1[0] * u + J.m1[1] * v < 0 && J.m2[0] * u + J.m2[1] * v < 0) return false;
        return true;
      }
      case 'ring':
        return inPolyL(J.ro, u, v) ? false : undefined;
      case 'barrel': {
        if (!inPolyL(J.ro, u, v)) return undefined;
        const b = bandOwner(J, w);
        if (inPolyL(J.ri, u, v)) {
          if (b !== role) return false;
          if (role === 'B') for (const h of J.holes) if (w > h[0] && w < h[1] && inPolyL(J.rHole, u, v)) return false;
          return true;
        }
        return inSeg && b === role;
      }
      default: { // 'v': cone hinge in a cavity
        if (!inPolyL(J.rc, u, v)) return undefined;
        const r = role === 'A' ? J.cav : J.clr;
        return inSeg && !(w > r[0] && w < r[1]);
      }
    }
  }

  // Candidate boundary pieces and splitting planes contributed by a joint's zone
  function jointFeatures(J, Zlo, Zhi) {
    const F = J.F, cutters = [], cands = [];
    const w0 = J.vertical ? Zlo : J.wLo - 0.5, w1 = J.vertical ? Zhi : J.wHi + 0.5;
    const G = (u, v, w) => toG(F, u, v, w);
    const nG = (a, b) => [a * F.U[0] + b * F.V[0], a * F.U[1] + b * F.V[1], a * F.U[2] + b * F.V[2]];
    for (const a of J.radii) for (let i = 0; i < SEG; i++) {
      const A = pvL(a, i), B = pvL(a, i + 1), mid = ((i + 0.5) * TAU) / SEG, N = nG(Math.cos(mid), Math.sin(mid));
      const poly = [G(A[0], A[1], w0), G(B[0], B[1], w0), G(B[0], B[1], w1), G(A[0], A[1], w1)];
      cands.push({ poly, N }); cutters.push({ N, d: dot3(N, poly[0]), bb: bbOf(poly) });
    }
    for (const r of J.rays) {
      const e = [(r.dir[0] * J.rmax), (r.dir[1] * J.rmax)], N = nG(r.m[0], r.m[1]);
      const poly = [G(0, 0, w0), G(e[0], e[1], w0), G(e[0], e[1], w1), G(0, 0, w1)];
      cands.push({ poly, N }); cutters.push({ N, d: dot3(N, poly[0]), bb: bbOf(poly) });
    }
    const Rm = J.rmax, rad = [0, ...J.radii];
    const zoneBB = bbOf([G(-Rm, -Rm, w0), G(Rm, -Rm, w0), G(Rm, Rm, w0), G(-Rm, Rm, w0), G(-Rm, -Rm, w1), G(Rm, -Rm, w1), G(Rm, Rm, w1), G(-Rm, Rm, w1)]);
    for (const w of J.levels) {
      if (w <= w0 || w >= w1) continue;
      const N = F.W.slice();
      cutters.push({ N, d: dot3(N, G(0, 0, w)), bb: bbOf([G(-Rm, -Rm, w), G(Rm, -Rm, w), G(Rm, Rm, w), G(-Rm, Rm, w)]) });
      for (let i = 0; i < SEG; i++) for (let b = 1; b < rad.length; b++) {
        const poly = rad[b - 1] === 0 ? [[0, 0], pvL(rad[b], i), pvL(rad[b], i + 1)] : [pvL(rad[b - 1], i), pvL(rad[b], i), pvL(rad[b], i + 1), pvL(rad[b - 1], i + 1)];
        cands.push({ poly: poly.map((p) => G(p[0], p[1], w)), N });
      }
    }
    return { cutters, cands, zoneBB };
  }
  // Candidate boundary pieces and splitting planes contributed by a joint's zone
  function jointFeatures(J, Zlo, Zhi) {
    const F = J.F, cutters = [], cands = [];
    const w0 = J.vertical ? Zlo : J.wLo - 0.5, w1 = J.vertical ? Zhi : J.wHi + 0.5;
    const G = (u, v, w) => toG(F, u, v, w);
    const nG = (a, b) => [a * F.U[0] + b * F.V[0], a * F.U[1] + b * F.V[1], a * F.U[2] + b * F.V[2]];
    for (const a of J.radii) for (let i = 0; i < SEG; i++) {
      const A = pvL(a, i), B = pvL(a, i + 1), mid = ((i + 0.5) * TAU) / SEG, N = nG(Math.cos(mid), Math.sin(mid));
      const poly = [G(A[0], A[1], w0), G(B[0], B[1], w0), G(B[0], B[1], w1), G(A[0], A[1], w1)];
      cands.push({ poly, N }); cutters.push({ N, d: dot3(N, poly[0]), bb: bbOf(poly) });
    }
    for (const r of J.rays) {
      const e = [(r.dir[0] * J.rmax), (r.dir[1] * J.rmax)], N = nG(r.m[0], r.m[1]);
      const poly = [G(0, 0, w0), G(e[0], e[1], w0), G(e[0], e[1], w1), G(0, 0, w1)];
      cands.push({ poly, N }); cutters.push({ N, d: dot3(N, poly[0]), bb: bbOf(poly) });
    }
    const Rm = J.rmax, rad = [0, ...J.radii];
    const zoneBB = bbOf([G(-Rm, -Rm, w0), G(Rm, -Rm, w0), G(Rm, Rm, w0), G(-Rm, Rm, w0), G(-Rm, -Rm, w1), G(Rm, -Rm, w1), G(Rm, Rm, w1), G(-Rm, Rm, w1)]);
    for (const w of J.levels) {
      if (w <= w0 || w >= w1) continue;
      const N = F.W.slice();
      cutters.push({ N, d: dot3(N, G(0, 0, w)), bb: bbOf([G(-Rm, -Rm, w), G(Rm, -Rm, w), G(Rm, Rm, w), G(-Rm, Rm, w)]) });
      for (let i = 0; i < SEG; i++) for (let b = 1; b < rad.length; b++) {
        const poly = rad[b - 1] === 0 ? [[0, 0], pvL(rad[b], i), pvL(rad[b], i + 1)] : [pvL(rad[b - 1], i), pvL(rad[b], i), pvL(rad[b], i + 1), pvL(rad[b - 1], i + 1)];
        cands.push({ poly: poly.map((p) => G(p[0], p[1], w)), N });
      }
    }
    return { cutters, cands, zoneBB };
  }
  // The slit of a cut: two V-shaped walls (A side and B side), finite across the body
  function cutFeatures(C, Zlo, Zhi) {
    const cutters = [], cands = [], pts = [];
    for (const s of [-1, 1]) for (const sg of [-1, 1]) {
      const L = sg < 0 ? C.Lneg : C.Lpos, off = C.g2 + C.tanA * L;
      const q0 = [C.px + C.n[0] * s * C.g2, C.py + C.n[1] * s * C.g2];
      const q1 = [C.px + C.t[0] * sg * L + C.n[0] * s * off, C.py + C.t[1] * sg * L + C.n[1] * s * off];
      const dx = q1[0] - q0[0], dy = q1[1] - q0[1], l = Math.hypot(dx, dy), N = [dy / l, -dx / l, 0];
      const poly = [[q0[0], q0[1], Zlo], [q1[0], q1[1], Zlo], [q1[0], q1[1], Zhi], [q0[0], q0[1], Zhi]];
      cands.push({ poly, N }); cutters.push({ N, d: N[0] * q0[0] + N[1] * q0[1], bb: bbOf(poly) });
      pts.push(...poly);
    }
    // the slit's two ends, where it stops across the body
    for (const sg of [-1, 1]) {
      const L = sg < 0 ? C.Lneg : C.Lpos, hw = C.g2 + C.tanA * L + 1e-3;
      const e = [C.px + C.t[0] * sg * L, C.py + C.t[1] * sg * L];
      const p0 = [e[0] - C.n[0] * hw, e[1] - C.n[1] * hw], p1 = [e[0] + C.n[0] * hw, e[1] + C.n[1] * hw];
      const N = [C.t[0] * sg, C.t[1] * sg, 0];
      const poly = [[p0[0], p0[1], Zlo], [p1[0], p1[1], Zlo], [p1[0], p1[1], Zhi], [p0[0], p0[1], Zhi]];
      cands.push({ poly, N }); cutters.push({ N, d: N[0] * e[0] + N[1] * e[1], bb: bbOf(poly) });
      pts.push(...poly);
    }
    return { cutters, cands, zoneBB: bbOf(pts) };
  }

  // Boundary faces: split every candidate piece by nearby planes, keep pieces with
  // the region on exactly one side, facing out of the region.
  function regionFaces(R) {
    const faces = [], eps = 1e-4;
    for (const cand of R.cands) {
      const cb = bbOf(cand.poly);
      let pieces = [cand.poly];
      for (const c of R.cutters) {
        if (!bbHit(cb, c.bb, 1e-6)) continue;
        if (Math.abs(Math.abs(dot3(c.N, cand.N)) - 1) < 1e-9 && Math.abs(dot3(c.N, cand.poly[0]) - c.d) < 1e-7) continue;
        const np = [];
        for (const pc of pieces) {
          let mn = Infinity, mx = -Infinity;
          for (const p of pc) { const sd = dot3(c.N, p) - c.d; if (sd < mn) mn = sd; if (sd > mx) mx = sd; }
          if (mn > -1e-9 || mx < 1e-9) { np.push(pc); continue; }
          const [a, b] = splitPoly(pc, c.N, c.d);
          if (a.length >= 3) np.push(a); if (b.length >= 3) np.push(b);
        }
        pieces = np;
      }
      const N = cand.N;
      for (const pc of pieces) {
        let cx = 0, cy = 0, cz = 0; for (const p of pc) { cx += p[0]; cy += p[1]; cz += p[2]; }
        cx /= pc.length; cy /= pc.length; cz /= pc.length;
        const a = R.inside(cx - N[0] * eps, cy - N[1] * eps, cz - N[2] * eps), b = R.inside(cx + N[0] * eps, cy + N[1] * eps, cz + N[2] * eps);
        if (a === b) continue;
        const No = a ? N : [-N[0], -N[1], -N[2]];
        const f = makeFace(No, dot3(No, pc[0]), pc);
        if (f) faces.push(f);
      }
    }
    return faces;
  }

  function capFace(mesh, face, cache, out, stats) {
    const F = face.F; const poly2 = face.poly2;
    // bbox of face
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (const p of face.poly3) { x0 = Math.min(x0, p[0]); y0 = Math.min(y0, p[1]); z0 = Math.min(z0, p[2]); x1 = Math.max(x1, p[0]); y1 = Math.max(y1, p[1]); z1 = Math.max(z1, p[2]); }
    const bb = mesh.bbox;
    if (x1 < bb.min[0] || x0 > bb.max[0] || y1 < bb.min[1] || y0 > bb.max[1] || z1 < bb.min[2] || z0 > bb.max[2]) return;
    let complex = false; const tmp = [];
    queryRect(mesh, x0 - 1e-6, y0 - 1e-6, x1 + 1e-6, y1 + 1e-6, (t) => {
      if (complex) return;
      if (mesh.B[t * 6 + 2] > z1 + 1e-6 || mesh.B[t * 6 + 5] < z0 - 1e-6) return;
      tmp.length = 0;
      if (!crossSegment(mesh, F, t, tmp)) return;
      if (segInConvex(tmp[0].a, tmp[0].b, poly2)) complex = true;
    });
    const emit = (tri2) => {
      const p = [to3(F, tri2[0][0], tri2[0][1]), to3(F, tri2[1][0], tri2[1][1]), to3(F, tri2[2][0], tri2[2][1])];
      out.push(p[0][0], p[0][1], p[0][2], p[1][0], p[1][1], p[1][2], p[2][0], p[2][1], p[2][2]);
    };
    if (!complex) {
      let cx = 0, cy = 0; for (const p of poly2) { cx += p[0]; cy += p[1]; } cx /= poly2.length; cy /= poly2.length;
      const c = to3(F, cx, cy);
      if (inside(mesh, c[0], c[1], c[2])) for (let i = 1; i + 1 < poly2.length; i++) emit([poly2[0], poly2[i], poly2[i + 1]]);
      return;
    }
    const key = F.N.map((v) => v.toFixed(9)).join(',') + '|' + F.d.toFixed(9);
    let tris = cache.get(key);
    if (!tris) {
      const sec = sectionLoops(mesh, F);
      stats.open += sec.open;
      tris = triangulateLoops(sec.loops); cache.set(key, tris);
    }
    for (const t of tris) {
      const c = clipConvex([[t[0], t[1]], [t[2], t[3]], [t[4], t[5]]], poly2);
      for (let i = 1; i + 1 < c.length; i++) emit([c[0], c[i], c[i + 1]]);
    }
  }

  function makeFace(N, d, poly3) {
    const F = planeFrame(N, d);
    let poly2 = poly3.map((p) => to2(F, p[0], p[1], p[2]));
    const a = polyArea2(poly2.flat());
    if (Math.abs(a) < 1e-9) return null;
    if (a < 0) { poly2 = poly2.reverse(); poly3 = poly3.slice().reverse(); }
    return { F, poly2, poly3 };
  }

  // Build segment surface = mesh \u2229 region, plus caps on the region's boundary faces
  function buildSegmentSurface(mesh, R, cache, stats) {
    const out = [];
    const { P, T, B, nt } = mesh;
    const planes = [], tb = [0, 0, 0, 0, 0, 0];
    for (let t = 0; t < nt; t++) {
      const a = T[t * 3] * 3, b = T[t * 3 + 1] * 3, c = T[t * 3 + 2] * 3;
      for (let k = 0; k < 6; k++) tb[k] = B[t * 6 + k];
      planes.length = 0;
      let V = null;
      for (const zn of R.zones) {
        if (!bbHit(tb, zn.bb, 1e-6)) continue;
        if (!V) V = [[P[a], P[a + 1], P[a + 2]], [P[b], P[b + 1], P[b + 2]], [P[c], P[c + 1], P[c + 2]]];
        for (const cu of zn.cutters) {
          if (!bbHit(tb, cu.bb, 1e-6)) continue;
          const s0 = dot3(cu.N, V[0]) - cu.d, s1 = dot3(cu.N, V[1]) - cu.d, s2 = dot3(cu.N, V[2]) - cu.d;
          if (Math.min(s0, s1, s2) < 0 && Math.max(s0, s1, s2) > 0) planes.push([cu.N, cu.d]);
        }
      }
      if (!planes.length) {
        const cx = (P[a] + P[b] + P[c]) / 3, cy = (P[a + 1] + P[b + 1] + P[c + 1]) / 3, cz = (P[a + 2] + P[b + 2] + P[c + 2]) / 3;
        if (R.inside(cx, cy, cz)) out.push(P[a], P[a + 1], P[a + 2], P[b], P[b + 1], P[b + 2], P[c], P[c + 1], P[c + 2]);
        continue;
      }
      let frags = [V];
      for (const [N, d] of planes) {
        const nf = [];
        for (const f of frags) {
          let mn = Infinity, mx = -Infinity; for (const p of f) { const sd = dot3(N, p) - d; if (sd < mn) mn = sd; if (sd > mx) mx = sd; }
          if (mn > -1e-10 || mx < 1e-10) { nf.push(f); continue; }
          const [fa, fb] = splitPoly(f, N, d); if (fa.length >= 3) nf.push(fa); if (fb.length >= 3) nf.push(fb);
        }
        frags = nf;
      }
      for (const f of frags) {
        let cx = 0, cy = 0, cz = 0; for (const p of f) { cx += p[0]; cy += p[1]; cz += p[2]; }
        cx /= f.length; cy /= f.length; cz /= f.length;
        if (!R.inside(cx, cy, cz)) continue;
        for (let i = 1; i + 1 < f.length; i++) out.push(f[0][0], f[0][1], f[0][2], f[i][0], f[i][1], f[i][2], f[i + 1][0], f[i + 1][1], f[i + 1][2]);
      }
    }
    for (const face of R.faces) capFace(mesh, face, cache, out, stats);
    return out;
  }

  // lathe a closed (rho, w) profile around a frame's W axis into triangle soup
  function lathe(profile, F, out) {
    const n = SEG, ring = (rho, w, i) => { const a = (i / n) * TAU; return toG(F, rho * Math.cos(a), rho * Math.sin(a), w); };
    const start = out.length, m = profile.length;
    for (let k = 0; k < m; k++) {
      const [r0, z0] = profile[k], [r1, z1] = profile[(k + 1) % m];
      if (r0 === 0 && r1 === 0) continue;
      for (let i = 0; i < n; i++) {
        const a0 = ring(r0, z0, i), a1 = ring(r0, z0, i + 1), b0 = ring(r1, z1, i), b1 = ring(r1, z1, i + 1);
        if (r0 === 0) out.push(...a0, ...b1, ...b0);
        else if (r1 === 0) out.push(...a0, ...a1, ...b0);
        else { out.push(...a0, ...a1, ...b1); out.push(...a0, ...b1, ...b0); }
      }
    }
    orientOutward(out, start);
  }
  function orientOutward(out, start) {
    let v = 0, ox = out[start] || 0, oy = out[start + 1] || 0, oz = out[start + 2] || 0;
    for (let i = start; i < out.length; i += 9) {
      const ax = out[i] - ox, ay = out[i + 1] - oy, az = out[i + 2] - oz, bx = out[i + 3] - ox, by = out[i + 4] - oy, bz = out[i + 5] - oz, cx = out[i + 6] - ox, cy = out[i + 7] - oy, cz = out[i + 8] - oz;
      v += ax * (by * cz - bz * cy) - ay * (bx * cz - bz * cx) + az * (bx * cy - by * cx);
    }
    if (v < 0) for (let i = start; i < out.length; i += 9) for (let k = 0; k < 3; k++) { const t = out[i + 3 + k]; out[i + 3 + k] = out[i + 6 + k]; out[i + 6 + k] = t; }
  }
  function box(corners8, out) { // two rings of 4 corners (start ring, end ring)
    const start = out.length; const f = [[0, 1, 2, 3], [7, 6, 5, 4], [0, 4, 5, 1], [1, 5, 6, 2], [2, 6, 7, 3], [3, 7, 4, 0]];
    for (const q of f) { const [a, b, c, d] = q.map((i) => corners8[i]); out.push(...a, ...c, ...b, ...a, ...d, ...c); }
    orientOutward(out, start);
  }
  // axis-aligned box in a vertical joint's (n, t, z) directions
  function boxNT(j, u0, u1, t0, t1, z0, z1, out) {
    const P = (u, t, z) => [j.px + j.n[0] * u + j.t[0] * t, j.py + j.n[1] * u + j.t[1] * t, z];
    box([P(u0, t0, z0), P(u1, t0, z0), P(u1, t1, z0), P(u0, t1, z0), P(u0, t0, z1), P(u1, t0, z1), P(u1, t1, z1), P(u0, t1, z1)], out);
  }

  // ---------------------------------------------------------------- joint sizes
  function jointDims(p) {
    const R = p.R, c = p.c;
    const r = Math.max(0.9, R * 0.42);
    const L = Math.max(1.2, R * 0.5);
    const T = Math.max(0.8, R * 0.28);
    const H = 2 * T + 2 * (R - r) + L;
    return { R, r, L, T, H, c, g: p.g, bend: p.bend, tanA: Math.tan((p.bend * Math.PI / 180) / 2) };
  }
  function jointLevels(J, D) {
    const zb = J.zb, zt = zb + D.H;
    const s = D.c * Math.SQRT2;
    const z2 = zb + D.T + D.R - D.r + D.L;
    return {
      zb, zt,
      cav: [zb + D.T, zt - D.T],
      clr: [zb - D.c, zt + D.c],
      axle: [[0, zb], [D.R, zb], [D.R, zb + D.T], [D.r, zb + D.T + D.R - D.r], [D.r, z2], [D.R, zt - D.T], [D.R, zt], [0, zt]],
      knuckle: [[D.R, zb + D.T + s], [D.R, zt - D.T - s], [D.r + D.c, z2 + D.c - s], [D.r + D.c, zb + D.T + D.R + s - D.r - D.c]],
      tabZ: [zb + D.T + s + 0.5, zt - D.T - s - 0.5],
    };
  }
  function ringDims(D) {
    const w = Math.max(1.6, 0.5 * D.R), rin = w / Math.SQRT2 + D.c + 0.15, rout = rin + Math.max(1.6, 0.5 * D.R);
    const tr = Math.max(1.2, 0.35 * D.R), tb = 1.2;
    return { w, rin, rout, ro: rout + D.c + 0.3, tr, tb, stack: 2 * tb + 2 * D.c + tr };
  }
  const LAP_A = 1.4;
  const lapNeedB = (D) => D.c + 1.2 + D.c + (D.R - D.r) + D.T + D.c + 0.8;
  // What a joint of this style needs where it sits. a: cut analysis (halfW, vLo, vHi)
  function zoneInfo(style, params, a) {
    const D = jointDims(params), c = D.c, g = D.g;
    const halfW = a && a.halfW != null ? a.halfW : 0;
    switch (style) {
      case 'lap': case 'tab': {
        const min = D.R + c + 1.5 + g;
        return { min, want: style === 'tab' ? min + 0.5 : Math.max(min, halfW + 1.2), thick: LAP_A + lapNeedB(D), width: 2 * (D.R + c) + 4 };
      }
      case 'ball': { const ri = D.R + c + 1.6; return { min: ri + g / 2, want: ri + g / 2, thick: 2 * (D.R + c) + 2, width: 2 * ri + 2 }; }
      case 'ring': { const rd = ringDims(D); return { min: rd.ro, want: rd.ro, thick: rd.stack, width: 2 * rd.ro + 1 }; }
      case 'barrel': {
        const rp = Math.max(1, 0.4 * D.R), half = a && a.vHi != null ? (a.vHi - a.vLo) / 2 : 4;
        const rho = 1.25 * half + 1 + g / 2;
        return { min: rho, want: rho, thick: 2 * (rp + c) + 2.4, width: 3 * (2 * rp + 2 + c) };
      }
      default: { const rc = D.R + c; return { min: rc + 1, want: rc + 1, thick: D.H + 0.8, width: 2 * rc + 2 }; }
    }
  }

  // ---------------------------------------------------------------- per-style joint set-up
  const PREP = {
    v(j) {
      const D = j.D;
      j.zb = Math.max(j.zlo, j.zc - D.H / 2);
      const Lv = jointLevels(j, D);
      j.Lv = Lv; j.cav = Lv.cav; j.clr = Lv.clr; j.rc = D.R + D.c;
      j.radii = [j.rc]; j.levels = [...Lv.cav, ...Lv.clr];
      j.axis.o = [j.px, j.py, (Lv.zb + Lv.zt) / 2];
    },
    lap(j) {
      const D = j.D, needB = lapNeedB(D);
      const slack = j.zhi - j.zlo - LAP_A - needB;
      j.zAt = j.zlo + LAP_A + Math.max(0, slack) / 2;
      j.zBb = j.zAt + D.c; j.zf = j.zBb + 1.2;
      const h0 = j.zf + D.c;
      j.zh1 = h0 + (D.R - D.r) + D.T; j.zCavTop = j.zh1 + D.c;
      j.zs0 = Math.max(j.zlo + 0.2, j.zAt - 1.2);
      j.ri = j.rho - D.g / 2; j.ro = j.rho + D.g / 2;
      j.rStemCav = D.r + D.c; j.rHeadCav = D.R + D.c;
      j.radii = [j.rStemCav, j.rHeadCav, j.ri, j.ro];
      j.levels = [j.zAt, j.zBb, j.zf, j.zCavTop];
      j.axleProfile = [[0, j.zs0], [D.r, j.zs0], [D.r, h0], [D.R, h0 + D.R - D.r], [D.R, j.zh1], [0, j.zh1]];
      j.seamless = j.rho >= j.rhoWant - 1e-6;
    },
    tab(j) { PREP.lap(j); j.seamless = false; },
    ball(j) {
      const D = j.D, Rb = D.R, c = D.c, hs = Math.max(0.8, 0.38 * Rb);
      const zc = Math.min(Math.max(j.zc, j.zlo + Rb + c + 1), Math.max(j.zlo + Rb + c + 1, j.zhi - Rb - c - 1));
      j.zb = zc; j.Rb = Rb; j.hs = hs;
      j.rCav = Rb + c; j.cav = [zc - Rb - c, zc + Rb + c]; j.slot = [zc - hs - c, zc + hs + c];
      j.ri = Rb + c + 1.6; j.ro = j.ri + D.g;
      const alpha = Math.min((80 * Math.PI) / 180, (D.bend * Math.PI) / 180 + Math.asin(Math.min(0.95, (hs + c) / (Rb + c))) + (3 * Math.PI) / 180);
      const e = [-j.n[0], -j.n[1]], f = [-e[1], e[0]], ta = Math.tan(alpha);
      const nrm = (v) => { const l = Math.hypot(v[0], v[1]); return [v[0] / l, v[1] / l]; };
      const ca = Math.cos(alpha), sa = Math.sin(alpha);
      j.m1 = nrm([f[0] - ta * e[0], f[1] - ta * e[1]]); j.m2 = nrm([-f[0] - ta * e[0], -f[1] - ta * e[1]]);
      j.rays = [{ dir: [ca * e[0] + sa * f[0], ca * e[1] + sa * f[1]], m: j.m1 }, { dir: [ca * e[0] - sa * f[0], ca * e[1] - sa * f[1]], m: j.m2 }];
      j.radii = [j.rCav, j.ri, j.ro]; j.levels = [...j.cav, ...j.slot];
      j.axis.o = [j.px, j.py, zc];
    },
    ring(j) {
      const D = j.D, rd = ringDims(D);
      const b0 = Math.max(j.zlo, Math.min(j.zc - rd.stack / 2, j.zhi - rd.stack));
      j.rd = rd; j.b0 = b0; j.z1 = b0 + rd.tb + D.c; j.z2 = j.z1 + rd.tr;
      j.ro = rd.ro; j.radii = [rd.ro]; j.levels = [];
      j.axis.o = [j.px, j.py, (j.z1 + j.z2) / 2];
    },
    barrel(j) {
      const D = j.D, c = D.c, half = (j.vHi - j.vLo) / 2, zA = (j.vHi + j.vLo) / 2;
      const rho = 1.25 * half + 1;
      j.ri = rho - D.g / 2; j.ro = rho + D.g / 2;
      j.F = { O: [j.px, j.py, zA], U: [j.n[0], j.n[1], 0], V: [0, 0, 1], W: [j.t[0], j.t[1], 0] };
      j.vertical = false; j.wLo = -j.Lneg; j.wHi = j.Lpos;
      const rp = Math.max(1, 0.4 * D.R), kl = Math.max(6, 2 * D.R + 2), span = j.wHi - j.wLo;
      let m = Math.max(3, Math.min(11, Math.round(span / kl))); if (m % 2 === 0) m += m < 11 ? 1 : -1;
      const bnd = []; for (let i = 1; i < m; i++) bnd.push(j.wLo + (span * i) / m);
      j.bands = [];
      for (let i = 0; i < m; i++) j.bands.push([i === 0 ? j.wLo - 1 : bnd[i - 1] + c / 2, i === m - 1 ? j.wHi + 1 : bnd[i] - c / 2, i % 2 ? 'B' : 'A']);
      const wb = span / m - c, Lp = Math.max(0.6, Math.min(rp + 1, (wb - c) / 2 - 0.3));
      j.rp = rp; j.rHole = rp + c; j.pins = []; j.holes = [];
      bnd.forEach((b, i) => {
        if (j.bands[i][2] === 'A') { j.pins.push([b - c / 2 - 1.0, b + c / 2 + Lp]); j.holes.push([b + c / 2, b + c / 2 + Lp + c]); }
        else { j.pins.push([b - c / 2 - Lp, b + c / 2 + 1.0]); j.holes.push([b - c / 2 - Lp - c, b - c / 2]); }
      });
      j.radii = [j.rHole, j.ri, j.ro];
      j.levels = [j.wLo, j.wHi, ...j.bands.flatMap((b) => [b[0], b[1]]), ...j.holes.flat()];
      j.axis = { o: [j.px, j.py, zA], d: [j.t[0], j.t[1], 0] };
      j.knuckles = m;
    },
  };

  // Pin and link geometry each side of a joint carries
  function jointParts(j) {
    const A = [], B = [], D = j.D;
    switch (j.style) {
      case 'lap': case 'tab': lathe(j.axleProfile, j.F, A); break;
      case 'ball': {
        const prof = [[0, j.zb - j.Rb]];
        for (let k = 1; k < 16; k++) { const th = -Math.PI / 2 + (Math.PI * k) / 16; prof.push([j.Rb * Math.cos(th), j.zb + j.Rb * Math.sin(th)]); }
        prof.push([0, j.zb + j.Rb]);
        lathe(prof, j.F, A);
        const hs = j.hs, s = [j.px, j.py], e = [j.px - j.n[0] * (j.ro + 1.5), j.py - j.n[1] * (j.ro + 1.5)];
        const ring = (p) => [[p[0] + j.t[0] * hs, p[1] + j.t[1] * hs, j.zb], [p[0], p[1], j.zb + hs], [p[0] - j.t[0] * hs, p[1] - j.t[1] * hs, j.zb], [p[0], p[1], j.zb - hs]];
        box([...ring(s), ...ring(e)], A);
        break;
      }
      case 'ring': {
        const rd = j.rd, c = D.c;
        lathe([[rd.rin, j.z1], [rd.rout, j.z1], [rd.rout, j.z2], [rd.rin, j.z2]], j.F, A);
        const nw = Math.max(2, rd.rout * 0.8) / 2;
        boxNT(j, -(rd.ro + 1.5), -(rd.rout - 0.5), -nw, nw, j.z1, j.z2, A);
        const h = rd.w / 2, top1 = j.z2 + c + rd.tb;
        boxNT(j, -h, h, -h, h, j.b0, top1, B);
        boxNT(j, -h, rd.ro + 1.5, -h, h, j.z2 + c, top1, B);
        boxNT(j, -h, rd.ro + 1.5, -h, h, j.b0, j.b0 + rd.tb, B);
        break;
      }
      case 'barrel':
        for (const [w0, w1] of j.pins) lathe([[0, w0], [j.rp, w0], [j.rp, w1], [0, w1]], j.F, A);
        break;
      default: {
        const Lv = j.Lv;
        lathe(Lv.axle, j.F, A);
        lathe(Lv.knuckle, j.F, B);
        const [z0, z1] = Lv.tabZ, w = D.R * 0.45;
        if (z1 > z0 + 0.2) boxNT(j, D.R - 0.5, D.R + D.c + 2.5, -w, w, z0, z1, B);
      }
    }
    return { A, B };
  }

  // ---------------------------------------------------------------- cut analysis
  // Cross-section where a cut sits: hinge point, local thickness/width, and how far the
  // cut must reach sideways (Lneg/Lpos) to cross the body part at the given point.
  function analyzeCut(mesh, px, py, phi, lateralHint) {
    const n = [Math.cos(phi), Math.sin(phi), 0];
    const F = planeFrame(n, n[0] * px + n[1] * py);
    const { loops } = sectionLoops(mesh, F);
    if (!loops.length) return { ok: false };
    const uP = to2(F, px, py, 0)[0];
    const info = loops.map((L) => {
      let lo = Infinity, hi = -Infinity, zl = Infinity, zh = -Infinity;
      for (let i = 0; i < L.length; i += 2) { lo = Math.min(lo, L[i]); hi = Math.max(hi, L[i]); zl = Math.min(zl, L[i + 1]); zh = Math.max(zh, L[i + 1]); }
      return { L, lo, hi, zl, zh, a: polyArea2(L) };
    });
    let best = null;
    for (const o of info) if (o.a > 0 && o.lo <= uP && o.hi >= uP && (!best || o.a > best.a)) best = o;
    if (!best) for (const o of info) if (o.a > 0 && (!best || o.a > best.a)) best = o;
    if (!best) return { ok: false };
    const set = info.filter((o) => o === best || !(o.hi < best.lo - 3 || o.lo > best.hi + 3));
    const cross = (vertical, c) => {
      const xs = [];
      for (const { L } of set) for (let i = 0, m = L.length / 2, j = m - 1; i < m; j = i++) {
        const ax = L[j * 2], ay = L[j * 2 + 1], bx = L[i * 2], by = L[i * 2 + 1];
        if (vertical) { if ((ax > c) !== (bx > c)) xs.push(ay + (by - ay) * (c - ax) / (bx - ax)); }
        else if ((ay > c) !== (by > c)) xs.push(ax + (bx - ax) * (c - ay) / (by - ay));
      }
      xs.sort((a, b) => a - b);
      const iv = []; for (let i = 0; i + 1 < xs.length; i += 2) iv.push([xs[i], xs[i + 1]]);
      return iv;
    };
    let uc = lateralHint != null ? uP + lateralHint : (best.lo + best.hi) / 2;
    let vz = cross(true, uc);
    if (!vz.length) { uc = (best.lo + best.hi) / 2; vz = cross(true, uc); }
    if (!vz.length) return { ok: false };
    vz.sort((a, b) => (b[1] - b[0]) - (a[1] - a[0]));
    const zI = vz[0], zc = (zI[0] + zI[1]) / 2;
    const hz = cross(false, zc);
    let wI = hz.find((iv) => iv[0] <= uc && iv[1] >= uc) || hz.sort((a, b) => (b[1] - b[0]) - (a[1] - a[0]))[0];
    if (!wI) wI = [best.lo, best.hi];
    if (lateralHint == null) uc = (wI[0] + wI[1]) / 2;
    let lo = Infinity, hi = -Infinity, vLo = Infinity, vHi = -Infinity;
    for (const o of set) { lo = Math.min(lo, o.lo); hi = Math.max(hi, o.hi); vLo = Math.min(vLo, o.zl); vHi = Math.max(vHi, o.zh); }
    const p3 = to3(F, uc, 0);
    return {
      ok: true, px: p3[0], py: p3[1], zc, zlo: zI[0], zhi: zI[1], thickness: zI[1] - zI[0], width: wI[1] - wI[0],
      lateral: uc - uP, halfW: Math.max(uc - lo, hi - uc), Lneg: uc - lo + 1.5, Lpos: hi - uc + 1.5, vLo, vHi, span: hi - lo,
    };
  }

  // ---------------------------------------------------------------- cut map
  // A top-down pixel map of the model split by every cut's slit. Each connected piece
  // becomes a segment; a cut joins the pieces on its two sides.
  function buildLabels(mesh, J) {
    const bb = mesh.bbox, M = 4;
    const X0 = bb.min[0] - M, Y0 = bb.min[1] - M, EX = bb.max[0] - bb.min[0] + 2 * M, EY = bb.max[1] - bb.min[1] + 2 * M;
    const s = Math.max(0.1, Math.max(EX, EY) / 1500);
    const gx = Math.ceil(EX / s) + 1, gy = Math.ceil(EY / s) + 1, NP = gx * gy;
    const mat = new Uint8Array(NP);
    const { P, T, nt } = mesh;
    for (let t = 0; t < nt; t++) {
      const a = T[t * 3] * 3, b = T[t * 3 + 1] * 3, c = T[t * 3 + 2] * 3;
      const ax = (P[a] - X0) / s, ay = (P[a + 1] - Y0) / s, bx = (P[b] - X0) / s, by = (P[b + 1] - Y0) / s, cx = (P[c] - X0) / s, cy = (P[c + 1] - Y0) / s;
      const ar = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
      if (Math.abs(ar) < 1e-12) continue;
      const i0 = Math.max(0, Math.ceil(Math.min(ax, bx, cx) - 0.5)), i1 = Math.min(gx - 1, Math.floor(Math.max(ax, bx, cx) - 0.5));
      const j0 = Math.max(0, Math.ceil(Math.min(ay, by, cy) - 0.5)), j1 = Math.min(gy - 1, Math.floor(Math.max(ay, by, cy) - 0.5));
      const sg = ar > 0 ? 1 : -1, e = -1e-9 * Math.abs(ar);
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        const x = i + 0.5, y = j + 0.5;
        const w0 = sg * ((bx - x) * (cy - y) - (cx - x) * (by - y)), w1 = sg * ((cx - x) * (ay - y) - (ax - x) * (cy - y)), w2 = sg * ((ax - x) * (by - y) - (bx - x) * (ay - y));
        if (w0 >= e && w1 >= e && w2 >= e) mat[j * gx + i] = 1;
      }
    }
    // grow by one pixel so points on the outline count as material
    const mat2 = mat.slice();
    for (let j = 1; j < gy - 1; j++) for (let i = 1; i < gx - 1; i++) if (!mat[j * gx + i]) {
      const k = j * gx + i;
      if (mat[k - 1] || mat[k + 1] || mat[k - gx] || mat[k + gx]) mat2[k] = 1;
    }
    // lengthen each slit until its whole end is clear of the body (the V widens as it goes)
    const matAt = (x, y) => { const i = Math.floor((x - X0) / s), j = Math.floor((y - Y0) / s); return i >= 0 && j >= 0 && i < gx && j < gy && mat2[j * gx + i]; };
    for (const q of J) for (const key of ['Lneg', 'Lpos']) {
      const sg = key === 'Lneg' ? -1 : 1;
      for (let guard = 0; guard < 400; guard++) {
        const L = q[key], hw = q.g2 + q.tanA * L + s, steps = Math.ceil((2 * hw) / (s * 0.5)) + 1;
        let hit = false;
        for (let k = 0; k <= steps && !hit; k++) {
          const a = -hw + (2 * hw * k) / steps;
          hit = matAt(q.px + q.t[0] * sg * L + q.n[0] * a, q.py + q.t[1] * sg * L + q.n[1] * a);
        }
        if (!hit) break;
        q[key] = L + s;
      }
    }
    const bar = new Uint8Array(NP);
    for (const q of J) {
      const ext = q.g2 + q.tanA * Math.max(q.Lneg, q.Lpos) + 2 * s;
      const cs = [];
      for (const l of [-q.Lneg, q.Lpos]) for (const a of [-ext, ext]) cs.push([q.px + q.t[0] * l + q.n[0] * a, q.py + q.t[1] * l + q.n[1] * a]);
      const i0 = Math.max(0, Math.floor((Math.min(...cs.map((p) => p[0])) - X0) / s)), i1 = Math.min(gx - 1, Math.ceil((Math.max(...cs.map((p) => p[0])) - X0) / s));
      const j0 = Math.max(0, Math.floor((Math.min(...cs.map((p) => p[1])) - Y0) / s)), j1 = Math.min(gy - 1, Math.ceil((Math.max(...cs.map((p) => p[1])) - Y0) / s));
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        const dx = X0 + (i + 0.5) * s - q.px, dy = Y0 + (j + 0.5) * s - q.py;
        const l = q.t[0] * dx + q.t[1] * dy; if (l < -q.Lneg || l > q.Lpos) continue;
        const a = q.n[0] * dx + q.n[1] * dy;
        if (Math.abs(a) < q.g2 + q.tanA * Math.abs(l) + 0.75 * s) bar[j * gx + i] = 1;
      }
    }
    // connected pieces of material
    const comp = new Int32Array(NP).fill(-1), area = [], queue = new Int32Array(NP);
    for (let k0 = 0; k0 < NP; k0++) {
      if (!mat2[k0] || bar[k0] || comp[k0] >= 0) continue;
      const id = area.length; let qh = 0, qt = 0, cnt = 0;
      queue[qt++] = k0; comp[k0] = id;
      while (qh < qt) {
        const k = queue[qh++]; cnt++;
        const i = k % gx;
        const nb = [i > 0 ? k - 1 : -1, i < gx - 1 ? k + 1 : -1, k - gx, k + gx];
        for (const m of nb) if (m >= 0 && m < NP && mat2[m] && !bar[m] && comp[m] < 0) { comp[m] = id; queue[qt++] = m; }
      }
      area.push(cnt);
    }
    const pix = (x, y) => { const i = Math.floor((x - X0) / s), j = Math.floor((y - Y0) / s); return i < 0 || j < 0 || i >= gx || j >= gy ? -1 : j * gx + i; };
    const probe = (q, sg) => {
      for (let k = 0; k < 16; k++) {
        const d = sg * (q.g2 + (2 + k) * s);
        const p = pix(q.px + q.n[0] * d, q.py + q.n[1] * d);
        if (p >= 0 && comp[p] >= 0) return comp[p];
      }
      return -1;
    };
    const rawA = J.map((q) => probe(q, -1)), rawB = J.map((q) => probe(q, 1));
    // keep touched pieces and real islands; merge specks into neighbours
    const keep = new Map(), minPix = 2 / (s * s);
    const seen = new Set([...rawA, ...rawB].filter((v) => v >= 0));
    area.forEach((a, id) => { if (seen.has(id) || a >= minPix) keep.set(id, keep.size); });
    const lab = new Int16Array(NP).fill(-1);
    let qh = 0, qt = 0;
    for (let k = 0; k < NP; k++) if (comp[k] >= 0 && keep.has(comp[k])) { lab[k] = keep.get(comp[k]); queue[qt++] = k; }
    while (qh < qt) {
      const k = queue[qh++], i = k % gx;
      const nb = [i > 0 ? k - 1 : -1, i < gx - 1 ? k + 1 : -1, k - gx, k + gx];
      for (const m of nb) if (m >= 0 && m < NP && !bar[m] && lab[m] < 0) { lab[m] = lab[k]; queue[qt++] = m; }
    }
    const segArea = new Array(keep.size).fill(0);
    for (const [id, nid] of keep) segArea[nid] += area[id] * s * s;
    const labelAt = (x, y) => {
      for (const q of J) {
        const dx = x - q.px, dy = y - q.py, l = q.t[0] * dx + q.t[1] * dy;
        if (l < -q.Lneg - 2 * s || l > q.Lpos + 2 * s) continue;
        const a = q.n[0] * dx + q.n[1] * dy, hw = q.g2 + q.tanA * Math.abs(l);
        if (Math.abs(a) >= hw + 3 * s) continue;
        if (l >= -q.Lneg && l <= q.Lpos && Math.abs(a) < hw) return -1;
        const a2 = (a < 0 ? -1 : 1) * (hw + 3 * s);
        x = q.px + q.n[0] * a2 + q.t[0] * l; y = q.py + q.n[1] * a2 + q.t[1] * l;
        break;
      }
      const p = pix(x, y);
      return p < 0 ? -2 : lab[p];
    };
    const mapId = (v) => (v >= 0 && keep.has(v) ? keep.get(v) : -1);
    return { labelAt, count: keep.size, area: segArea, compA: rawA.map(mapId), compB: rawB.map(mapId), s };
  }

  // Largest joint size (up to the chosen one) that fits inside the body at this cut
  const MIN_R = 1.6;
  function fitJoint(style, params, a) {
    const avail = a && a.zhi != null ? a.zhi - a.zlo : Infinity, wid = a && a.width != null ? a.width : Infinity;
    let R = params.R;
    for (;;) {
      const z = zoneInfo(style, { ...params, R }, a);
      if (z.thick <= avail + 1e-6 && z.width <= wid + 1e-6) return { R, fits: true, needThick: z.thick };
      if (R <= MIN_R + 1e-9) return { R, fits: false, needThick: z.thick, needWidth: z.width };
      R = Math.max(MIN_R, Math.round((R - 0.1) * 10) / 10);
    }
  }
  // ---------------------------------------------------------------- preparing joints
  // joints: [{px,py,phi,zc,zlo,zhi,halfW,Lneg,Lpos,vLo,vHi,style?}]; params: {R,c,g,bend,style}
  function prepJoints(mesh, joints, params) {
    const D = jointDims(params);
    const J = joints.map((j0, idx) => {
      // nudge by a fraction of a micron so hinge planes never line up exactly with model faces
      const j = { ...j0, px: j0.px + 3.1e-4, py: j0.py + 2.3e-4, zc: j0.zc + 1.7e-4, vLo: j0.vLo != null ? j0.vLo - 1.3e-4 : j0.vLo, vHi: j0.vHi != null ? j0.vHi + 1.9e-4 : j0.vHi, Lneg: j0.Lneg + 1.1e-4, Lpos: j0.Lpos + 1.4e-4 };
      const style = j.style || params.style || 'lap';
      const n = [Math.cos(j.phi), Math.sin(j.phi)], t = [-n[1], n[0]];
      const fit = fitJoint(style, params, j);
      const Dj = jointDims({ ...params, R: fit.R });
      // keep the slit narrow: never wider than edgeGap at the far edges of the cut
      const Lmax = Math.max(1, j.Lneg || 0, j.Lpos || 0);
      let tanA = style === 'barrel' ? 0 : Dj.tanA;
      if (params.edgeGap != null && style !== 'barrel') tanA = Math.min(tanA, Math.max(0, (params.edgeGap - Dj.g) / 2) / Lmax);
      Dj.tanA = tanA; Dj.bend = Math.max(1, (2 * Math.atan(tanA) * 180) / Math.PI);
      return {
        ...j, idx, style, D: Dj, R: fit.R, fits: fit.fits, needThick: fit.needThick, n, t, g2: Dj.g / 2, tanA,
        bendMax: style === 'barrel' ? (params.bend * Math.PI) / 180 : 2 * Math.atan(tanA),
        vertical: true, wLo: -Infinity, wHi: Infinity, F: frameV(j.px, j.py),
        radii: [], levels: [], rays: [], bands: [], holes: [], axis: { o: [j.px, j.py, j.zc], d: [0, 0, 1] },
      };
    });
    for (const j of J) if (j.style === 'lap' || j.style === 'tab') { const zi = zoneInfo(j.style, { ...params, R: j.R }, j); j.rho = j.rhoWant = zi.want; j.rhoMin = zi.min; }
    // a barrel's knuckles span the body everywhere its hinge zone reaches, not just on the cut line
    for (const j of J) if (j.style === 'barrel') {
      const rho = 1.25 * (j.vHi - j.vLo) / 2 + 1 + D.g;
      for (const off of [-rho, -rho / 2, rho / 2, rho]) {
        const a = analyzeCut(mesh, j.px + j.n[0] * off, j.py + j.n[1] * off, j.phi, null);
        if (!a.ok) continue;
        const dt = j.t[0] * (a.px - j.px) + j.t[1] * (a.py - j.py);
        if (dt - a.Lneg > j.Lpos || dt + a.Lpos < -j.Lneg) continue;
        j.Lneg = Math.max(j.Lneg, a.Lneg - dt); j.Lpos = Math.max(j.Lpos, dt + a.Lpos);
        j.vLo = Math.min(j.vLo, a.vLo); j.vHi = Math.max(j.vHi, a.vHi);
      }
    }
    for (let a = 0; a < J.length; a++) for (let b = a + 1; b < J.length; b++) {
      const p = J[a], q = J[b];
      if (p.rho == null || q.rho == null) continue;
      const room = Math.hypot(p.px - q.px, p.py - q.py) - D.g - 1.0, sum = p.rho + q.rho;
      if (sum > room) { const f = Math.max(0, room) / sum; p.rho = Math.max(p.rhoMin, p.rho * f); q.rho = Math.max(q.rhoMin, q.rho * f); }
    }
    for (const j of J) PREP[j.style] ? PREP[j.style](j) : PREP.v(j);
    // barrel pins only where the body surrounds them
    for (const j of J) if (j.style === 'barrel') {
      const ok = (w0, w1) => {
        for (let k = 0; k <= 4; k++) {
          const w = w0 + ((w1 - w0) * k) / 4;
          for (const [u, v] of [[0, 0], [j.rHole + 0.8, 0], [-j.rHole - 0.8, 0], [0, j.rHole + 0.8], [0, -j.rHole - 0.8]]) {
            const p = toG(j.F, u, v, w); if (!inside(mesh, p[0], p[1], p[2])) return false;
          }
        }
        return true;
      };
      const keep = j.pins.map((pn, i) => ok(Math.min(pn[0], j.holes[i][0]), Math.max(pn[1], j.holes[i][1])));
      j.pins = j.pins.filter((_, i) => keep[i]); j.holes = j.holes.filter((_, i) => keep[i]);
      j.levels = [j.wLo, j.wHi, ...j.bands.flatMap((b) => [b[0], b[1]]), ...j.holes.flat()];
      j.pinCount = j.pins.length;
    }
    for (const j of J) {
      j.radii = uniqSorted(j.radii); j.levels = uniqSorted(j.levels); j.rmax = j.radii[j.radii.length - 1] / COSH + 1e-6;
      if (j.style === 'lap' && j.seamless) j.bendMax = Math.max(j.bendMax, (50 * Math.PI) / 180);
    }
    return J;
  }

  function buildRegion(LB, J, c) {
    const adj = [], zones = [];
    for (const j of J) {
      const rA = j.compA === c, rB = j.compB === c;
      if (!rA && !rB) continue;
      if (!j.broken) adj.push({ J: j, role: rA ? 'A' : 'B' });
      zones.push(j.cutF);
      if (!j.broken) zones.push(j.zoneF);
    }
    const inside = (x, y, z) => {
      const inSeg = LB.labelAt(x, y) === c;
      for (const e of adj) { const o = jointOwn(e.J, e.role, inSeg, x, y, z); if (o !== undefined) return o; }
      return inSeg;
    };
    const R = { inside, zones: zones.map((f) => ({ bb: f.zoneBB, cutters: f.cutters })), cutters: zones.flatMap((f) => f.cutters), cands: zones.flatMap((f) => f.cands) };
    R.faces = regionFaces(R);
    return R;
  }

  // Which pieces a set of cuts makes, without building anything (for the joint list)
  function cutReport(mesh, joints, params) {
    const J = prepJoints(mesh, joints, params);
    const LB = buildLabels(mesh, J);
    return { count: LB.count, broken: J.map((j, i) => LB.compA[i] < 0 || LB.compB[i] < 0 || LB.compA[i] === LB.compB[i]) };
  }

  // ---------------------------------------------------------------- main
  async function makeFlexi(mesh, joints, params, onProgress) {
    const J = prepJoints(mesh, joints, params);
    const LB = buildLabels(mesh, J);
    const bb = mesh.bbox, Zlo = bb.min[2] - 1, Zhi = bb.max[2] + 1;
    for (const j of J) {
      j.compA = LB.compA[j.idx]; j.compB = LB.compB[j.idx];
      j.broken = j.compA < 0 || j.compB < 0 || j.compA === j.compB;
      j.cutF = cutFeatures(j, Zlo, Zhi);
      j.zoneF = jointFeatures(j, Zlo, Zhi);
    }
    const K = LB.count, segs = [], stats = { open: 0 }, cache = new Map();
    const partsOf = J.map((j) => (j.broken ? { A: [], B: [] } : jointParts(j)));
    for (let c = 0; c < K; c++) {
      if (onProgress) await onProgress(c / K, 'Cutting piece ' + (c + 1) + ' of ' + K);
      const R = buildRegion(LB, J, c);
      const body = buildSegmentSurface(mesh, R, cache, stats);
      const parts = [];
      J.forEach((j, i) => {
        if (j.broken) return;
        const src = j.compA === c ? partsOf[i].A : j.compB === c ? partsOf[i].B : null;
        if (src) for (let k = 0; k < src.length; k++) parts.push(src[k]);
      });
      if (onProgress) await onProgress((c + 0.6) / K, 'Stitching piece ' + (c + 1) + ' of ' + K);
      segs.push({ body: cleanSoup(fixTJunctions(new Float32Array(body))), parts: new Float32Array(parts), area: LB.area[c] });
    }
    let root = 0; segs.forEach((s, i) => { if (s.area > segs[root].area) root = i; });
    if (onProgress) await onProgress(1, 'Done');
    for (const j of J) { delete j.cutF; delete j.zoneF; }
    return { segments: segs, joints: J, root, openLoops: stats.open };
  }

  // Pin and link geometry for one prepared joint, for the plan view
  function jointPreview(j) {
    const p = jointParts(j);
    return { A: new Float32Array(p.A), B: new Float32Array(p.B) };
  }

  // ---------------------------------------------------------------- flatten to outline
  // Outline of the model seen from above, the side or the front, extruded flat.
  function marching(img, gx, gy) {
    const val = (i, j) => (i < 0 || j < 0 || i >= gx || j >= gy ? 0 : img[j * gx + i]);
    const W = gx + 3, next = new Map(), pos = new Map();
    const H = (i, j) => (j * W + i) * 2, Vk = (i, j) => (j * W + i) * 2 + 1;
    for (let j = -1; j < gy; j++) for (let i = -1; i < gx; i++) {
      const c = [val(i, j), val(i + 1, j), val(i + 1, j + 1), val(i, j + 1)];
      if (c[0] === c[1] && c[1] === c[2] && c[2] === c[3]) continue;
      // edges counter-clockwise: bottom, right, top, left
      const ek = [H(i + 1, j + 1), Vk(i + 2, j + 1), H(i + 1, j + 2), Vk(i + 1, j + 1)];
      const ep = [[i + 1, j + 0.5], [i + 1.5, j + 1], [i + 1, j + 1.5], [i + 0.5, j + 1]];
      const type = [0, 1, 2, 3].map((k) => (c[k] && !c[(k + 1) % 4] ? 'out' : !c[k] && c[(k + 1) % 4] ? 'in' : null));
      for (let k = 0; k < 4; k++) if (type[k] === 'out') {
        for (let d = 1; d < 4; d++) { const k2 = (k + d) % 4; if (type[k2] === 'in') { next.set(ek[k], ek[k2]); pos.set(ek[k], ep[k]); pos.set(ek[k2], ep[k2]); break; } }
      }
    }
    const loops = [], used = new Set();
    for (const k0 of next.keys()) {
      if (used.has(k0)) continue;
      const L = []; let k = k0, guard = 0;
      while (!used.has(k) && next.has(k) && guard++ < 1e7) { used.add(k); const p = pos.get(k); L.push(p[0], p[1]); k = next.get(k); }
      if (L.length >= 6) loops.push(L);
    }
    return loops;
  }
  function simplifyLoop(L, eps) {
    const n = L.length / 2; if (n < 6) return L;
    let far = 0, fd = -1;
    for (let i = 1; i < n; i++) { const d = (L[i * 2] - L[0]) ** 2 + (L[i * 2 + 1] - L[1]) ** 2; if (d > fd) { fd = d; far = i; } }
    const keep = new Uint8Array(n); keep[0] = keep[far] = 1;
    const stack = [[0, far], [far, n]];
    const P = (i) => [L[(i % n) * 2], L[(i % n) * 2 + 1]];
    while (stack.length) {
      const [a, b] = stack.pop(); if (b - a < 2) continue;
      const A = P(a), B = P(b), dx = B[0] - A[0], dy = B[1] - A[1], l = Math.hypot(dx, dy) || 1e-12;
      let bi = -1, bd = eps;
      for (let i = a + 1; i < b; i++) { const p = P(i), d = Math.abs((p[0] - A[0]) * dy - (p[1] - A[1]) * dx) / l; if (d > bd) { bd = d; bi = i; } }
      if (bi >= 0) { keep[bi % n] = 1; stack.push([a, bi], [bi, b]); }
    }
    const o = []; for (let i = 0; i < n; i++) if (keep[i]) o.push(L[i * 2], L[i * 2 + 1]);
    return o.length >= 6 ? o : L;
  }
  function flattenSilhouette(soup, view, thick) {
    const [ia, ib] = view === 'top' ? [0, 1] : view === 'front' ? [1, 2] : [0, 2];
    const mn = [Infinity, Infinity, Infinity], mx = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < soup.length; i += 3) for (let k = 0; k < 3; k++) { const v = soup[i + k]; if (v < mn[k]) mn[k] = v; if (v > mx[k]) mx[k] = v; }
    const A0 = mn[ia], B0 = mn[ib], EA = mx[ia] - A0, EB = mx[ib] - B0;
    const s = Math.max(0.05, Math.max(EA, EB) / 1400), pad = 3;
    const gx = Math.ceil(EA / s) + 2 * pad + 1, gy = Math.ceil(EB / s) + 2 * pad + 1;
    let img = new Uint8Array(gx * gy);
    for (let t = 0; t < soup.length; t += 9) {
      const ax = (soup[t + ia] - A0) / s + pad, ay = (soup[t + ib] - B0) / s + pad, bx = (soup[t + 3 + ia] - A0) / s + pad, by = (soup[t + 3 + ib] - B0) / s + pad, cx = (soup[t + 6 + ia] - A0) / s + pad, cy = (soup[t + 6 + ib] - B0) / s + pad;
      const ar = (bx - ax) * (cy - ay) - (cx - ax) * (by - ay); if (Math.abs(ar) < 1e-12) continue;
      const sg = ar > 0 ? 1 : -1, e = -1e-9 * Math.abs(ar);
      const i0 = Math.max(0, Math.ceil(Math.min(ax, bx, cx) - 0.5)), i1 = Math.min(gx - 1, Math.floor(Math.max(ax, bx, cx) - 0.5));
      const j0 = Math.max(0, Math.ceil(Math.min(ay, by, cy) - 0.5)), j1 = Math.min(gy - 1, Math.floor(Math.max(ay, by, cy) - 0.5));
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
        const x = i + 0.5, y = j + 0.5;
        if (sg * ((bx - x) * (cy - y) - (cx - x) * (by - y)) >= e && sg * ((cx - x) * (ay - y) - (ax - x) * (cy - y)) >= e && sg * ((ax - x) * (by - y) - (bx - x) * (ay - y)) >= e) img[j * gx + i] = 1;
      }
    }
    // close pinholes and hairline cracks
    const morph = (src, grow) => {
      const o = src.slice();
      for (let j = 1; j < gy - 1; j++) for (let i = 1; i < gx - 1; i++) {
        const k = j * gx + i, nb = src[k - 1] + src[k + 1] + src[k - gx] + src[k + gx] + src[k - gx - 1] + src[k - gx + 1] + src[k + gx - 1] + src[k + gx + 1];
        if (grow && !src[k] && nb) o[k] = 1; if (!grow && src[k] && nb < 8) o[k] = 0;
      }
      return o;
    };
    img = morph(morph(img, true), false);
    let loops = marching(img, gx, gy).map((L) => {
      const o = new Array(L.length);
      for (let i = 0; i < L.length; i += 2) { o[i] = A0 + (L[i] - pad) * s; o[i + 1] = B0 + (L[i + 1] - pad) * s; }
      return simplifyLoop(o, 0.35 * s);
    }).filter((L) => Math.abs(polyArea2(L)) > 9 * s * s);
    if (!loops.length) throw new Error('No outline found from this direction.');
    const tris = triangulateLoops(loops);
    const cx = (mn[0] + mx[0]) / 2, cy = (mn[1] + mx[1]) / 2, z0 = mn[2], z1 = z0 + thick;
    const ma = A0 + EA / 2, mb = B0 + EB / 2;
    const map = (a, b) => (view === 'top' ? [a, b] : view === 'side' ? [a, cy + (b - mb)] : [cx + (a - ma), cy + (b - mb)]);
    const out = [];
    for (const t of tris) {
      const p = [map(t[0], t[1]), map(t[2], t[3]), map(t[4], t[5])];
      out.push(p[0][0], p[0][1], z1, p[1][0], p[1][1], z1, p[2][0], p[2][1], z1);
      out.push(p[0][0], p[0][1], z0, p[2][0], p[2][1], z0, p[1][0], p[1][1], z0);
    }
    for (const L of loops) {
      const m = L.length / 2;
      for (let i = 0; i < m; i++) {
        const a = map(L[i * 2], L[i * 2 + 1]), b = map(L[((i + 1) % m) * 2], L[((i + 1) % m) * 2 + 1]);
        out.push(a[0], a[1], z0, b[0], b[1], z0, b[0], b[1], z1);
        out.push(a[0], a[1], z0, b[0], b[1], z1, a[0], a[1], z1);
      }
    }
    return new Float32Array(out);
  }

  return { fitJoint, buildLabels, zoneInfo, cutReport, flattenSilhouette, prepJoints, jointPreview, sectionLoops, planeFrame, fixTJunctions, parseSTL, parse3MF, makeMesh, makeFlexi, analyzeCut, jointDims, toSTL, to3MF, zip, unzip, weld, openEdges, inside, earcut, finishMesh, triangulateLoops };
})();
export default FX;
